import React, { useEffect, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import { AUTHH, BASE_URL } from '../../../config/axios';
import { formatDate, formattedDateLong } from '../../Date';
import { CheckTick, CircularGrid, CrossRed, Filter, Office, WarnYellow, Attachment, Eye, DocumentIcon, BlueEye, CloudUpload } from '../../AllSvg';
import { DateRangePicker, formatCurrencyIndian, formatDateTime, getMonthName, handleErrorToast, inputMaxLimit, inputMinLimit, ViewDocumnetsbilling, ViewFile, CustomTooltip, generateCustomFileForUpload, AttachmentIcon } from '../../CustomFunctions';
import { Modal } from 'react-bootstrap';
import ProofViewModal from "../../CustomTable/ProofViewModal";


const DynamicRequestLogTimeline = ({ i, detailedCheckKeys }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Function to format timestamp
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return 'null';
        if (typeof value === 'object') {
            // Handle nested objects in dynamic_approval
            if (value.id && (value.role || value.employee)) {
                return value.role
                    ? `Role: ${value.role.role_name} (${value.role.id})`
                    : `Employee: ${value.employee.name} (${value.employee.id})`;
            }
            return JSON.stringify(value, null, 2);
        }
        // Handle long strings (not just URLs)
        if (typeof value === 'string' && value.length > 50) {
            return `${value.substring(0, 25)}...${value.substring(value.length - 25)}`;
        }
        return value.toString();
    };

    const renderObjectValue = (value) => {
        if (value === null) return 'null';
        if (typeof value !== 'object') return value;

        return (
            <div className="nested-object">
                {Object.entries(value).map(([k, v]) => (
                    <div key={k} className="nested-pair">
                        <span className="nested-key">{k}:</span>
                        <span className="nested-value">
                            {typeof v === 'object' ? renderObjectValue(v) : v}
                        </span>
                    </div>
                ))}
            </div>
        );
    };


    const getChangedFields = (prevObj, currentObj) => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            // Skip these fields as they're not relevant changes
            if (['created_at', 'updated_at', 'id', 'dynamic_approval', 'other_details'].includes(key)) return;

            const prevValue = prevObj[key];
            const currentValue = currentObj[key];

            // Handle nested objects (like role, employee)
            if (typeof prevValue === 'object' && prevValue !== null && !Array.isArray(prevValue)) {
                const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue || {});
                if (nestedChanges.length > 0) {
                    changedFields.push({
                        field: key,
                        from: JSON.stringify(prevValue),
                        to: JSON.stringify(currentValue)
                    });
                }
                return;
            }

            // Compare arrays (like other_details)
            if (Array.isArray(prevValue)) {
                // Simple comparison for arrays - could be enhanced
                if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                    changedFields.push({
                        field: key,
                        from: 'Previous array values',
                        to: 'Updated array values'
                    });
                }
                return;
            }

            // Compare primitive values
            if (prevValue !== currentValue) {
                changedFields.push({
                    field: key,
                    from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
                    to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
                });
            }
        });

        return changedFields;
    };

    const getDetailedChangedFields = (prevObj, currentObj, parentKey = '') => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            const prevValue = prevObj[key];
            const currentValue = currentObj[key];
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            // Handle nested objects
            if (typeof prevValue === 'object' && prevValue !== null && !Array.isArray(prevValue)) {
                const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue || {}, fullKey);
                changedFields.push(...nestedChanges);
                return;
            }

            // Handle arrays (deep comparison)
            if (Array.isArray(prevValue)) {
                if (!Array.isArray(currentValue) || prevValue.length !== currentValue.length ||
                    JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                    changedFields.push({
                        field: fullKey,
                        from: JSON.stringify(prevValue),
                        to: JSON.stringify(currentValue)
                    });
                }
                return;
            }

            // Handle primitive values
            if (prevValue !== currentValue) {
                changedFields.push({
                    field: fullKey,
                    from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
                    to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
                });
            }
        });

        return changedFields;
    };

    const getConditionalDetailChangedFields = (prevObj, currentObj, parentKey = '', detailedCheckKeys = []) => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            const prevValue = prevObj[key];
            const currentValue = currentObj[key];
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            // Skip always-ignored system fields
            if (['created_at', 'updated_at', 'id'].includes(key)) return;

            // Check if the field should be deeply inspected
            const shouldDeepCheck = detailedCheckKeys.includes(key) || detailedCheckKeys.includes(parentKey);

            // Handle nested objects or arrays
            if (typeof prevValue === 'object' && prevValue !== null && currentValue !== null) {
                if (shouldDeepCheck) {
                    const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue, fullKey, detailedCheckKeys);
                    changedFields.push(...nestedChanges);
                } else {
                    if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                        changedFields.push({
                            field: fullKey,
                            from: JSON.stringify(prevValue),
                            to: JSON.stringify(currentValue)
                        });
                    }
                }
                return;
            }

            // Primitive values comparison
            // if (prevValue !== currentValue) {
            //     changedFields.push({
            //         field: fullKey,
            //         from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
            //         to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
            //     });
            // }

            if (prevValue !== currentValue) {
                changedFields.push({
                    field: parentKey ? `${parentKey}.${key}` : key,
                    from: formatValue(prevValue),
                    to: formatValue(currentValue)
                });
            }
        });

        return changedFields;
    };


    return (
        <>
            <button title="View Logs Timeline" onClick={handleShow} className="log-timeline-button">
                <Eye />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="log-timeline-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Change Log Timeline</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="timeline-container">
                        {i?.change_log?.length > 1 ? (
                            <div className="timeline">
                                {i.change_log.map((logEntry, index) => {
                                    const prevLog = index > 0 ? i.change_log[index - 1].log : null;
                                    const detailedCheckKeys = ['other_details', 'dynamic_approval'];
                                    const changes = getConditionalDetailChangedFields(prevLog, logEntry.log, '', detailedCheckKeys);

                                    if (changes.length === 0) return null;

                                    return (
                                        <div key={index} className={`timeline-item ${index === 0 ? 'first' : ''} ${index === i.change_log.length - 1 ? 'last' : ''}`}>
                                            <div className="timeline-marker"></div>
                                            <div className="timeline-content">
                                                <div className="timeline-header">
                                                    <span className="timeline-date">{formatDate(logEntry.changed_at)}</span>
                                                    <span className="badge">{changes.length} changes</span>
                                                </div>

                                                <div className="changes-list">
                                                    {changes.map((change, changeIndex) => (
                                                        <div key={changeIndex} className="change-item">
                                                            <div className="change-field">{change.field}:</div>

                                                            <div className="change-details">
                                                                <span className="from-value">{change.from}</span>
                                                                <span className="arrow">→</span>
                                                                {/* <span className="to-value">{change.to}</span> */}
                                                                <span className="to-value">
                                                                    {typeof change.to === 'object' ? renderObjectValue(change.to) : change.to}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="no-logs">No change logs available</div>
                        )}
                    </div>

                </Modal.Body>
            </Modal>
        </>
    );
};


const DynamicRequestImprestApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
    });

    // console.log("formData- ", formData)
    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        let ImprestFormData = {}
        let approved_other_details = formData.other_details;

        ImprestFormData = {
            approved_stationary_item: formData.approved_stationary_item,
            approved_electronic_item: formData.approved_electronic_item,
            approved_grocery: formData.approved_grocery,
            other_details: approved_other_details,
        };

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        // console.log("ImprestFormData")
        // console.log(ImprestFormData)
        // console.log("ApprovalFormData")
        // console.log(ApprovalFormData)

        try {
            let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`,
                ImprestFormData,
            );

            if (imp.status === 200 || imp.status === 201) {
                toast.success("Imprest Updated Successfully");
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovalspermissionsimprest/${i.id}/`, ApprovalFormData,);

                if (req.status === 200 || req.status === 201) {
                    toast.success("Imprest Request Status Updated Successfully");
                    await getDynamicRequestData();
                } else {
                    toast.error(`Imprest Request Update Error: ${req.statusText}`);
                    console.log('Imprest Request Update Error:')
                    console.error(req);

                }
            } else {
                toast.error(`Imprest Update Error: ${imp.statusText}`);
                console.log('Imprest Update Error:')
                console.error(imp);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };
    console.log("i-", i)

    return (
        <>
            <button className="model-edit-button-ml" title={`Imprest Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Imprest "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.imprest_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <li className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.project_code}-{i.imprest_details?.project_short_name}
                                </li>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Total Expense -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(
                                        Number(formData.approved_stationary_item) +
                                        Number(formData.approved_electronic_item) +
                                        Number(formData.approved_grocery) +
                                        (formData.other_details?.length > 0
                                            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                                            : 0)
                                    )}
                                </div>
                            </div>


                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="transportation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Stationary -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_stationary_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="accomodation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Electronics -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_electronic_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="flex-row">
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Grocery -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_grocery)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                <div className="flex-column">
                                    {formData.other_details?.map((other, index) => (

                                        i.imprest_details?.other_details[index].amount > 0 && (
                                            <div style={{ paddingLeft: '0', marginLeft: '0', listStylePosition: 'inside' }}>
                                                <div key={index} className="form-labels announce-date font-size-heading">
                                                    <span className="text-capitalize">
                                                        {other.other_name ? other.other_name : `Other-${index + 1}`} : {formatCurrencyIndian(other.approved_other)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.no_of_days}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Remarks :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.remarks}
                                </div>
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestImprestEditApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
            amount_change_reason: "",
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
        amount_change_reason: "",
    });

    //******************Validation Start********************* */
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["amount_change_reason"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('approved_')) {

            // For individual expenses like transportation, accommodation, fooding, etc.
            if (!name.startsWith('approved_other_')) {
                const itemAmountKey = name.replace('approved_', '');
                const currentValue = i[itemAmountKey] !== undefined && i[itemAmountKey] !== null ? i[itemAmountKey] : 0;
                const maxLength = getNumberLength(currentValue);

                // Ensure the input value does not exceed the allowed length
                if (value.length <= maxLength) {
                    const numericValue = value.trim() ? parseInt(value, 10) : 0;

                    // Check if the numeric value is less than or equal to the current value
                    if (numericValue <= currentValue) {
                        setInputState({
                            ...inputState,
                            [name]: value.trim() ? "success" : "",
                        });

                        setFormData({
                            ...formData,
                            [name]: value,
                        });

                        // Clear any previous error
                        setErrors({
                            ...errors,
                            [name]: "",
                        });
                    } else {
                        // Handle case where the numeric value exceeds the current value
                        setErrors({
                            ...errors,
                            [name]: `Value must be less than or equal to ${currentValue}`,
                        });
                    }
                } else {
                    // Handle case where input length exceeds the allowed length
                    setErrors({
                        ...errors,
                        [name]: `Maximum allowed length is ${maxLength} digits`,
                    });
                }

            } else {
                // For entries in approved_other_details
                const index = parseInt(name.replace('approved_other_', ''), 10);
                const currentOther = i.imprest_details?.other_details[index];
                const currentValue = currentOther.amount !== undefined && currentOther.amount !== null ? currentOther.amount : 0;
                const maxLength = getNumberLength(currentValue);

                // Ensure the input value does not exceed the allowed length
                if (value.length <= maxLength) {
                    const numericValue = value.trim() ? parseInt(value, 10) : 0;

                    // Check if the numeric value is less than or equal to the current value
                    if (numericValue <= currentValue) {
                        const newOtherDetails = [...formData.other_details];
                        newOtherDetails[index].approved_other = value;

                        setInputState({
                            ...inputState,
                            [name]: value.trim() ? "success" : "",
                        });

                        setFormData({
                            ...formData,
                            other_details: newOtherDetails,
                        });

                        // Clear any previous error
                        setErrors({
                            ...errors,
                            [name]: "",
                        });
                    } else {
                        // Handle case where the numeric value exceeds the current value
                        setErrors({
                            ...errors,
                            [name]: `Value must be less than or equal to ${currentValue}`,
                        });
                    }
                } else {
                    // Handle case where input length exceeds the allowed length
                    setErrors({
                        ...errors,
                        [name]: `Maximum allowed length is ${maxLength} digits`,
                    });
                }
            }

        } else {
            // For textarea or other fields without length validation
            setInputState({
                ...inputState,
                [name]: value.trim() ? "success" : "",
            });

            setFormData({
                ...formData,
                [name]: value,
            });
            setErrors({
                ...errors,
                [name]: "",
            });
        }
    };

    function getNumberLength(value) {
        return value?.toString()?.length;
    }

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait...");
            let ImprestFormData = {}
            let approved_other_details = formData.other_details;

            ImprestFormData = {
                amount_change_reason: formData.amount_change_reason,
                approved_stationary_item: formData.approved_stationary_item,
                approved_electronic_item: formData.approved_electronic_item,
                approved_grocery: formData.approved_grocery,
                other_details: approved_other_details,
            };

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };


            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`,
                    ImprestFormData,
                );

                if (imp.status === 200 || imp.status === 201) {
                    toast.success("Imprest Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                        console.log('Imprest Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                    console.log('Imprest Update Error:')
                    console.error(imp);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId);
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-partial-button" title={`Imprest Stage ${i.level} Editable Approval`} onClick={handleShow}>
                <WarnYellow />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h1 className="task-Tab-heading align-center font-weight500 font-size-heading">Give Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approvable Amount for Imprest : "{i.imprest_details?.imprest_name}" </h1>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.imprest_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <li className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.project_code}-{i.imprest_details?.project_short_name}
                                </li>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.no_of_days}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Remarks :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.remarks}
                                </div>
                            </div>
                            <hr className="line" />
                            <div className="flex-column">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Total Expense<span className='required'>(Auto)</span>
                                </label>
                                <input
                                    id="total"
                                    type="number"
                                    placeholder="0"
                                    value={
                                        Number(formData.approved_stationary_item) +
                                        Number(formData.approved_electronic_item) +
                                        Number(formData.approved_grocery) +
                                        // Number(formData.approved_other)
                                        (formData.other_details?.length > 0
                                            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                                            : 0)
                                    }
                                    readOnly
                                    className="form-input font-weight500 font-size-subheading"
                                    style={{ cursor: 'not-allowed' }}
                                />
                            </div>
                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="flex-column">
                                    <label htmlFor="approved_stationary_item" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                        Stationary
                                    </label>
                                    <input
                                        id="approved_stationary_item"
                                        type="number"
                                        name="approved_stationary_item"
                                        placeholder="0"
                                        min={0}
                                        maxLength={getNumberLength(i.imprest_details?.stationary_item)}
                                        max={i.imprest_details.stationary_item}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_stationary_item}
                                        className={`form-input ${errors.approved_stationary_item ? "error" : inputState.approved_stationary_item ? "success" : ""}`}
                                    />
                                    {errors.approved_stationary_item && (
                                        <span className="error-message font-size-text">{errors.approved_stationary_item}</span>
                                    )}
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="flex-column">
                                    <label htmlFor="approved_electronic_item" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                        Electronics
                                    </label>
                                    <input
                                        id="approved_electronic_item"
                                        type="number"
                                        name="approved_electronic_item"
                                        placeholder="0"
                                        min={0}
                                        max={i.imprest_details.electronic_item}
                                        maxLength={getNumberLength(i.imprest_details.electronic_item)}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_electronic_item}
                                        className={`form-input ${errors.approved_electronic_item ? "error" : inputState.approved_electronic_item ? "success" : ""}`}
                                    />
                                    {errors.approved_electronic_item && (
                                        <span className="error-message font-size-text">{errors.approved_electronic_item}</span>
                                    )}
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="flex-column">
                                    <label htmlFor="approved_grocery" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                        Grocery
                                    </label>
                                    <input
                                        id="approved_grocery"
                                        type="number"
                                        name="approved_grocery"
                                        placeholder="0"
                                        min={0}
                                        max={i.imprest_details.approved_grocery}
                                        maxLength={getNumberLength(i.imprest_details.grocery)}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_grocery}
                                        className={`form-input ${errors.approved_grocery ? "error" : inputState.approved_grocery ? "success" : ""}`}
                                    />
                                    {errors.approved_grocery && (
                                        <span className="error-message font-size-text">{errors.approved_grocery}</span>
                                    )}
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                <div className="flex-column">
                                    {formData.other_details?.map((other, index) => (
                                        i.imprest_details.other_details[index].amount > 0 && (
                                            <div key={index} className="flex-column">
                                                <label htmlFor={`approved_other_${index}`} className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                                    {other.other_name ? other.other_name : `Other-${index + 1}`} :
                                                </label>
                                                <input
                                                    id={`approved_other_${index}`}
                                                    type="number"
                                                    name={`approved_other_${index}`}
                                                    placeholder="0"
                                                    min={0}
                                                    max={i.imprest_details.other_details[index].amount}
                                                    onWheel={(e) => e.target.blur()}
                                                    onChange={handleInputChange}
                                                    value={other.approved_other}
                                                    className={`form-input ${errors[`approved_other_${index}`] ? "error" : inputState[`approved_other_${index}`] ? "success" : ""}`}
                                                />
                                                {errors[`approved_other_${index}`] && <span className="error-message font-size-text">{errors[`approved_other_${index}`]}</span>}
                                            </div>
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="flex-column">
                                <label htmlFor="amount_change_reason" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Amount Change Reason<span className="required">*</span>
                                </label>
                                <textarea
                                    id="amount_change_reason"
                                    type="text"
                                    name="amount_change_reason"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.amount_change_reason}
                                    className={`form-input-textarea font-weight400 font-size-subheading ${errors.amount_change_reason ? "error" : inputState.amount_change_reason ? "success" : ""}`}
                                />
                                {errors.amount_change_reason && (
                                    <span className="error-message font-size-text">{errors.amount_change_reason}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-submit font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal>
        </>
    );
};

const DynamicRequestImprestFinalize = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),

            account_approved_final_details: "",
            account_transaction_proof: null,
            imprest_open: true,
        })
        setShow(true)
    };


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),

        account_approved_final_details: "",
        account_transaction_proof: null,
        imprest_open: true,
    });

    const totalApproved =
        i.imprest_details?.change_log?.length > 1
            ?
            (Number(i.imprest_details?.approved_stationary_item) +
                Number(i.imprest_details?.approved_electronic_item) +
                Number(i.imprest_details?.approved_grocery) +
                // Number(i.approved_other)
                (i.imprest_details?.other_details?.length > 0
                    ? i.imprest_details?.other_details?.reduce((total, other) => total + Number(other.approved_other), 0)
                    : 0))
            : (
                Number(formData.approved_stationary_item) +
                Number(formData.approved_electronic_item) +
                Number(formData.approved_grocery) +
                (formData.other_details?.length > 0
                    ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                    : 0)
            );

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields =
            totalApproved === 0 ? [] :
                [
                    "account_approved_final_details",
                    "account_transaction_proof"
                ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            account_transaction_proof: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            const ImprestFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            if (formData.account_transaction_proof && formData.account_approved_final_details) {
                const originalFile = formData?.account_transaction_proof;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ImprestFormData.append("account_approved_final_details", formData.account_approved_final_details);
                ImprestFormData.append("account_transaction_proof", customFile);
            }
            ImprestFormData.append("imprest_open", formData.imprest_open);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`, ImprestFormData, AUTHH, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (imp.status === 200 || imp.status === 201) {
                    toast.success("Imprest Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                        console.log('Imprest Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                    console.log('Imprest Update Error:')
                    console.error(imp);
                }

                // if (imp.status === 200) {
                //     await getDynamicRequestData();
                //     setShow(false);
                // } else {
                //     alert(imp);
                // }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-edit-button-ml" title={`Imprest Stage ${i.level} Finalize Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Finalize Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Imprest "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.imprest_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <li className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.project_code}-{i.imprest_details?.project_short_name}
                                </li>
                            </div>


                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Total Expense -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(
                                        Number(formData.approved_stationary_item) +
                                        Number(formData.approved_electronic_item) +
                                        Number(formData.approved_grocery) +
                                        (formData.other_details?.length > 0
                                            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                                            : 0)
                                    )}
                                </div>
                            </div>

                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="transportation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Stationary -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_stationary_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="accomodation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Electronics -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_electronic_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="flex-row">
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Grocery -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_grocery)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                <div className="flex-column">
                                    {formData.other_details?.map((other, index) => (

                                        i.imprest_details?.other_details[index].amount > 0 && (
                                            <div style={{ paddingLeft: '0', marginLeft: '0', listStylePosition: 'inside' }}>
                                                <div key={index} className="form-labels announce-date font-size-heading">
                                                    <span className="text-capitalize">
                                                        {other.other_name ? other.other_name : `Other-${index + 1}`} : {formatCurrencyIndian(other.approved_other)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.no_of_days}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Remarks :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.remarks}
                                </div>
                            </div>


                            {totalApproved == 0 ? null :
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="account_approved_final_details"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Final Approval Details (Transaction / Cheque no.)
                                        </label>
                                        <input
                                            id="account_approved_final_details"
                                            name="account_approved_final_details"
                                            placeholder="Final Approval Details (Transaction / Cheque no.)"
                                            onChange={handleInputChange}
                                            value={formData.account_approved_final_details}
                                            className={`form-input ${errors.account_approved_final_details
                                                ? "error"
                                                : inputState.account_approved_final_details
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.account_approved_final_details && (
                                            <span className="error-message font-size-text ">{errors.account_approved_final_details}</span>
                                        )}
                                    </div>

                                    <div className="flex-row">
                                        <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                            Account Transaction Proof :
                                        </label>
                                        <div className="flex-column" >
                                            <label
                                                htmlFor="account_transaction_proof"
                                                className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.account_transaction_proof
                                                        ? "error"
                                                        : inputState.account_transaction_proof
                                                            ? "success"
                                                            : ""
                                                    }`}
                                            >
                                                <div className="svg-field form-input align-center">
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                id="account_transaction_proof"
                                                name="account_transaction_proof"
                                                type="file"
                                                onChange={handleFileChange}
                                                onFocus={handleClear}
                                                className="file-inputs align-center"
                                                style={{ display: "none", position: "relative" }}
                                            />
                                            <br />
                                            <div className="flex-row justify-evenly">
                                                <div>
                                                    {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                                </div>
                                            </div>
                                        </div>
                                        {formData.account_transaction_proof ?
                                            <div>
                                                <button className="model-button" onClick={handleClear}>Clear</button>
                                            </div>
                                            : null
                                        }
                                        {errors.account_transaction_proof && (
                                            <span className="error-message font-size-text ">{errors.account_transaction_proof}</span>
                                        )}
                                    </div>
                                </>
                            }
                            <br />

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestImprestRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
            remarks: "",
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
        remarks: "",
    })

    //******************Valedation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            let RejectedStatus = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {

                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, RejectedStatus);

                if (req.status === 200 || req.status === 201) {
                    toast.success("Imprest Request Status Updated Successfully");
                    await getDynamicRequestData();
                } else {
                    toast.error(`Imprest Request Update Error: ${req.statusText}`);
                    console.log('Imprest Request Update Error:')
                    console.error(req);

                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-delete-button" title={`Imprest Stage ${i.level} Rejection`} onClick={handleShow}>
                <CrossRed />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Rejection of Imprest "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.imprest_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <li className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.project_code}-{i.imprest_details?.project_short_name}
                                </li>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Total Expense -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {
                                        Number(i.imprest_details?.approved_stationary_item) +
                                        Number(i.imprest_details?.approved_electronic_item) +
                                        Number(i.imprest_details?.approved_grocery) +
                                        (i.imprest_details?.other_details?.length > 0
                                            ? i.imprest_details?.other_details?.reduce((total, other) => total + Number(other.approved_other), 0)
                                            : 0)
                                    }
                                </div>
                            </div>

                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="transportation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Stationary -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_stationary_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="flex-row">
                                    <label
                                        htmlFor="accomodation"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Electronics -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_electronic_item)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="flex-row">
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Grocery -
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {formatCurrencyIndian(formData.approved_grocery)}
                                    </div>
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                <div className="flex-column">
                                    {formData.other_details?.map((other, index) => (

                                        i.imprest_details?.other_details[index].amount > 0 && (
                                            <div style={{ paddingLeft: '0', marginLeft: '0', listStylePosition: 'inside' }}>
                                                <div key={index} className="form-labels announce-date font-size-heading">
                                                    <span className="text-capitalize">
                                                        {other.other_name ? other.other_name : `Other-${index + 1}`} : {formatCurrencyIndian(other.approved_other)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.no_of_days}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Remarks :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.imprest_details?.remarks}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Rejection Reason
                                </label>
                                <textarea
                                    id="remarks"
                                    type="text"
                                    name="remarks"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.remarks}
                                    className={`form-input-textarea   font-weight400  font-size-subheading ${errors.remarks
                                        ? "error"
                                        : inputState.remarks
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message font-size-text ">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-delete  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};

const DynamicRequestExpenseApproval = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +
        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0)

    const [loading, setLoading] = useState(false); //loading logic

    const handleDynamicApproval = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        setLoading(true); //loading logic

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        try {

            let exp = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: false,
                utilized_amount: sum,
                remaining_amount: total - sum,
            });

            if (exp.status === 200 || exp.status === 201) {
                toast.success("Expense Updated Successfully");

                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${k.id}/`, ApprovalFormData,);
                if (req.status === 200 || req.status === 201) {
                    toast.success("Expense Request Status Updated Successfully");
                    handleClose()
                    // await getExpenseData();
                    toast.info("Navigating to Expense Requests Page");
                    handleBackRoute()
                } else {
                    toast.error(`Expense Request Update Error: ${req.statusText}`);
                    console.log('Expense Request Update Error:')
                    console.error(req);

                }
            } else {
                toast.error(`Expense Update Error: ${exp.statusText}`);
                console.log('Expense Update Error:')
                console.error(exp);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            setLoading(false); //loading logic
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="model-button  font-weight500 model-button-submit" title="TL Expense Approve" onClick={handleShow}>
                Approve
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {k.level} {k.employee ? (k.role ? `${k.employee?.name}(${k.role?.role_name})` : `${k.employee?.name}`) : k.role?.role_name} Approval of Expense : "{i.name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.date)}
                                </div>


                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Expense against Imprest :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.project_name}
                                </div>
                            </div>

                            {/* <hr className="line" /> */}
                            <hr className="line" />
                            <div className="children">
                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Utilized Amount
                                    </label>
                                    <p className="utilize   font-weight500   -amount align-center" style={{ color: "red" }}>
                                        {/* {i.utilized_amount} */}
                                        {sum}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Remaining Amount
                                    </label>
                                    <p className="remaining-amount  font-weight500    align-center" style={{ color: "#2576bc" }}>
                                        {/* {i.remaining_amount} */}
                                        {total - sum}
                                    </p>
                                </div>
                            </div>



                            <div className="button-models">
                                {!loading && <button className="model-button model-button-cancel  font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>}
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleDynamicApproval}
                                    disabled={loading}
                                >
                                    {loading ?
                                        <div className='flex-row'>
                                            Approving...
                                            <div className="spinner-vsmall"></div>
                                        </div>
                                        : "Approve"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestExpensePartialEdit = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +
        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0)

    const [loading, setLoading] = useState(false); //loading logic

    const handleDynamicEditRejected = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        setLoading(true); //loading logic

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: false,
            });

            if (res.status === 200) {
                toast.success("Expense Updated Successfully");
                // await getExpenseData();
                toast.info("Navigating to Expense Requests Page");
                handleBackRoute();
            } else {
                toast.error(`Expense Update Error: ${res.statusText}`);
                console.log('Expense Update Error:')
                console.error(res);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            setLoading(false); //loading logic
            toast.dismiss(loadingToastId);
        }
    };

    const handleDynamicEditApproval = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        setLoading(true); //loading logic

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: true,
            });

            if (res.status === 200) {
                toast.success("Expense Updated Successfully");
                // await getExpenseData();
                toast.info("Navigating to Expense Requests Page");
                handleBackRoute();

            } else {
                toast.error(`Expense Update Error: ${res.statusText}`);
                console.log('Expense Update Error:')
                console.error(res);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            setLoading(false); //loading logic
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="model-button  font-weight500 model-button-pending" title="Account A Expense Approve" onClick={handleShow}>
                {/* Edit Access */}
                Permit Bill Update
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {k.level} {k.employee ? (k.role ? `${k.employee?.name}(${k.role?.role_name})` : `${k.employee?.name}`) : k.role?.role_name} Approval for Bill Modification in Expense : "{i.name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.date)}
                                </div>


                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Expense against Imprest :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.project_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Current No. of Bills :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {j.length}
                                </div>
                            </div>

                            {/* <hr className="line" /> */}
                            <hr className="line" />
                            <div className="children">
                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Utilized Amount
                                    </label>
                                    <p className="utilize   font-weight500   -amount align-center" style={{ color: "red" }}>
                                        {sum}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Remaining Amount
                                    </label>
                                    <p className="remaining-amount  font-weight500    align-center" style={{ color: "#2576bc" }}>
                                        {total - sum}
                                    </p>
                                </div>
                            </div>



                            <div className="button-models">
                                {/* <button className="model-button model-button-cancel  font-weight500" onClick={handleClose}>
                  Cancel
                </button> */}
                                {
                                    i.partially_change ?
                                        <button
                                            type="submit"
                                            className="model-button font-weight500 model-button-delete  font-weight500    font-size-heading"
                                            onClick={handleDynamicEditRejected}
                                        >
                                            {loading ?
                                                <div className='flex-row'>
                                                    Locking...
                                                    <div className="spinner-vsmall"></div>
                                                </div>
                                                : "Lock Edit"}
                                        </button>
                                        :
                                        <button
                                            type="submit"
                                            className="model-button font-weight500 model-button-cancel  font-weight500    font-size-heading"
                                            onClick={handleDynamicEditApproval}
                                        >
                                            {loading ?
                                                <div className='flex-row'>
                                                    Unlocking...
                                                    <div className="spinner-vsmall"></div>
                                                </div>
                                                : "Unlock Edit"}                                        </button>

                                }
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestExpenseFinalize = ({ i, j, getDynamicRequestData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const handleShow = async () => {
        setFormData({
            decision: "",
            settlement: "",
            settlement_amount: "",
            settlement_bill: "",
        })
        setShow(true)
    };
    const handleClose = () => {
        setShow(false)
    };

    const [formData, setFormData] = useState({
        decision: "",
        settlement: "",
        settlement_amount: "",
        settlement_bill: "",
    });

    const [fileName, setFileName] = useState("");

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "decision",
            formData.decision === 'Paid' || formData.decision === 'Salary' ? "settlement_amount" : null,
            formData.decision === 'Paid' ? "settlement_bill" : null,
        ].filter(Boolean);

        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            // settlement_amount: "",
            settlement_bill: null,
        }));
        setFileName("");
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        // if (name === "decision" && value !== "Paid") {
        //   handleClear();
        // }

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const [loading, setLoading] = useState(false); //loading logic

    const handleAccountFinalize = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            setLoading(true); //loading logic

            const ExpenseFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }

            if (formData.settlement_bill) {
                const originalFile = formData.settlement_bill;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ExpenseFormData.append("settlement_bill", customFile);
                ExpenseFormData.append("settlement", true);
            }
            ExpenseFormData.append("decision", formData.decision);
            ExpenseFormData.append("partially_change", false);
            ExpenseFormData.append("settlement_amount", formData.settlement_amount);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let exp = await axios.put(`${BASE_URL}/siteproject/expense/${j.id}/`, ExpenseFormData);

                if (exp.status === 200) {
                    toast.success("Expense Updated Successfully");

                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Expense Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Expense Request Update Error: ${req.statusText}`);
                        console.log('Expense Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Expense Update Error: ${exp.statusText}`);
                    console.log('Expense Update Error:')
                    console.error(exp);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId);
            } finally {
                setLoading(false); //loading logic
                toast.dismiss(loadingToastId);
            }
        }
    };

    // console.log("FormData")
    // console.log(formData)

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <button className="model-edit-button-ml" title="Accounts Final Decision" onClick={handleShow}>
                <CheckTick />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Expense : "{j.name}" </h6>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(j.date)}
                                </div>


                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Expense against Imprest :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {j.imprest_data?.imprest_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    For Project-
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {j.imprest_data?.project_short_name}
                                </div>
                            </div>

                            {/* <hr className="line" /> */}
                            <hr className="line" />
                            <div className="children">
                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Utilized Amount
                                    </label>
                                    <p className="utilize   font-weight500   -amount align-center" style={{ color: "red" }}>
                                        {j.utilized_amount}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Remaining Amount
                                    </label>
                                    <p className="remaining-amount  font-weight500    align-center" style={{ color: "#2576bc" }}>
                                        {j.remaining_amount}
                                    </p>
                                </div>
                            </div>
                            <hr className="line" />

                            <div className="flex-column form-group-selectt">
                                <label htmlFor="decision" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Final Decision
                                </label>
                                <br />
                                <select
                                    id="decision"
                                    name="decision"
                                    value={formData.decision}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        handleClear();
                                    }}
                                    className={`form-input form-group-selection ${errors.decision ? "error" : formData.decision ? "success" : ""}`}
                                >
                                    <option value="">Select Decision</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Salary">To Salary</option>
                                    <option value="Balance">To Balance</option>
                                </select>

                            </div>
                            <br />
                            {errors.decision && (
                                <span className="error-message font-size-text ">{errors.decision}</span>
                            )}


                            {formData.decision === 'Paid' && (
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="settlement_amount"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Settlement Amount
                                        </label>
                                        <input
                                            id="settlement_amount"
                                            name="settlement_amount"
                                            type="number"
                                            min={0}
                                            placeholder="Settlement Amount"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={handleInputChange}
                                            value={formData.settlement_amount}
                                            className={`form-input ${errors.settlement_amount
                                                ? "error"
                                                : inputState.settlement_amount
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.settlement_amount && (
                                            <span className="error-message font-size-text ">{errors.settlement_amount}</span>
                                        )}
                                    </div>

                                    <div className="flex-row">
                                        <label htmlFor="decision" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                            Settlement Bill :
                                        </label>
                                        <div className="flex-column" >
                                            <label
                                                htmlFor="settlement_bill"
                                                className={`align-center  form-inputss custom-file-upload 
                      ${errors.settlement_bill
                                                        ? "error"
                                                        : inputState.settlement_bill
                                                            ? "success"
                                                            : ""
                                                    }
                         `}
                                            >
                                                <div className={`align-center pdf-input  ${errors.settlement_bill ? 'error' : ''}`}>
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                id="settlement_bill"
                                                name="settlement_bill"
                                                type="file"
                                                onChange={handleFileChange}
                                                onFocus={handleClear}
                                                className="file-inputs align-center"
                                                style={{ display: "none", position: "relative" }}
                                            />
                                            <br />
                                            <div className="flex-row justify-evenly">
                                                <div>
                                                    {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                                </div>
                                            </div>
                                        </div>
                                        {formData.settlement_bill ?
                                            <div>
                                                <button className="model-button" onClick={handleClear}>Clear</button>
                                            </div>
                                            : null
                                        }
                                        {errors.settlement_bill && (
                                            <span className="error-message font-size-text ">{errors.settlement_bill}</span>
                                        )}
                                    </div>

                                </>
                            )}

                            {formData.decision === 'Salary' && (
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="settlement_amount"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Settlement Amount
                                        </label>
                                        <input
                                            id="settlement_amount"
                                            name="settlement_amount"
                                            type="number"
                                            min={0}
                                            placeholder="Settlement Amount"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={handleInputChange}
                                            value={formData.settlement_amount}
                                            className={`form-input ${errors.settlement_amount
                                                ? "error"
                                                : inputState.settlement_amount
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.settlement_amount && (
                                            <span className="error-message font-size-text ">{errors.settlement_amount}</span>
                                        )}
                                    </div>
                                </>
                            )}
                            <br />
                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-submit  font-weight500    font-size-heading"
                                    onClick={handleAccountFinalize}
                                    disabled={loading}
                                >
                                    {loading ?
                                        <div className='flex-row'>
                                            Submitting...
                                            <div className="spinner-vsmall"></div>
                                        </div>
                                        : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestExpenseRejection = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +
        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0)

    const [formData, setFormData] = useState({
        remarks: "",
    });

    // console.log('k- ', k)
    //******************Valedation Start********************* */

    const [loading, setLoading] = useState(false); //loading logic
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const handleDynamicRejection = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            setLoading(true); //loading logic


            let RejectionFormData = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let exp = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                    submit_for_approval: k.level === 1 ? false : true,
                    utilized_amount: sum,
                    remaining_amount: total - sum,
                });

                if (exp.status === 200 || exp.status === 201) {
                    toast.success("Expense Updated Successfully");

                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${k.id}/`, RejectionFormData,);
                    if (req.status === 200 || req.status === 201) {
                        toast.success("Expense Request Status Updated Successfully");
                        handleClose()
                        // await getExpenseData();
                        toast.info("Navigating to Expense Requests Page");
                        handleBackRoute()
                    } else {
                        toast.error(`Expense Request Update Error: ${req.statusText}`);
                        console.log('Expense Request Update Error:')
                        console.error(req);
                    }
                } else {
                    toast.error(`Expense Update Error: ${exp.statusText}`);
                    console.log('Expense Update Error:')
                    console.error(exp);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                setLoading(false); //loading logic
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="model-button  font-weight500 model-button-delete" title="TL Expense Reject" onClick={handleShow}>
                Reject
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {k.level} {k.employee ? (k.role ? `${k.employee?.name}(${k.role?.role_name})` : `${k.employee?.name}`) : k.role?.role_name} Rejection of Expense : "{i.name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Expense against Imprest :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.project_name}
                                </div>
                            </div>

                            {/* <hr className="line" /> */}
                            <hr className="line" />
                            <div className="children">
                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Utilized Amount
                                    </label>
                                    <p className="utilize   font-weight500   -amount align-center" style={{ color: "red" }}>
                                        {sum}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Remaining Amount
                                    </label>
                                    <p className="remaining-amount  font-weight500    align-center" style={{ color: "#2576bc" }}>
                                        {total - sum}
                                    </p>
                                </div>
                            </div>
                            <hr className="line" />


                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Rejection Reason
                                </label>
                                <textarea
                                    id="remarks"
                                    type="text"
                                    name="remarks"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.remarks}
                                    className={`form-input-textarea   font-weight400  font-size-subheading ${errors.remarks
                                        ? "error"
                                        : inputState.remarks
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message font-size-text ">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                {!loading && <button className="model-button model-button-cancel  font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>}
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-delete  font-weight500    font-size-heading"
                                    onClick={handleDynamicRejection}
                                    disabled={loading}
                                >
                                    {loading ? "Rejecting..." : "Reject"}

                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestOfficeApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        let ApprovalFormData = {
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };

        try {
            let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);
            if (req.status === 200 || req.status === 201) {
                toast.success("Office Request Status Updated Successfully");
                await getDynamicRequestData();
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };
    console.log("i-", i)


    return (
        <>
            <button className="model-edit-button-ml" title={`Office Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Office "{i?.officerent_billing_details?.owner_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i?.officerent_billing_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.officerent_billing_details?.project_short_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Rent Amount :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Tds :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_amt || 0)} ({i?.officerent_billing_details?.tds_percentage}%)
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Total Amount :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}
                                </div>
                            </div>




                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.officerent_billing_details?.days}
                                </div>
                            </div>



                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};
const DynamicBillingFinal = ({ i, getofficebillingdata, documentlist }) => {

    console.log(i, "i final")
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
    });


    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "settlement_transaction_id",
            "settlement_bill"
        ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            settlement_bill: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();

        const formDataa = new FormData();

        const symbols = '!@#$%^&*()_-+=';
        const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const now = new Date();
        const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
        const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
        const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * allChars.length);
            randomCode += allChars[randomIndex];
        }
        if (formData.settlement_bill && formData.settlement_transaction_id) {
            const originalFile = formData?.settlement_bill;
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
            const customFile = new File([originalFile], customFileName, { type: originalFile.type });

            formDataa.append("settlement_transaction_id", formData.settlement_transaction_id);
            formDataa.append("settlement_bill", customFile);
        }

        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };
            try {
                let res = await axios.put(`${BASE_URL}/siteproject/officerentbillingupdate/${i?.officerent_billing_details?.id}/`, formDataa, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (res.status === 200 || res.status === 201) {
                    toast.success("Office Billing Updated Successfully");

                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);
                    if (req.status === 200 || req.status === 201) {
                        toast.success("Office Request Status Updated Successfully");
                        await getofficebillingdata();
                        setShow(false);
                    } else {
                        toast.error(`Office  Request Update Error: ${req.statusText}`);
                        console.log('Office  Request Update Error:')
                        console.error(req);

                    }


                } else {
                    toast.error(`Office Update Error: ${res.statusText}`);
                    console.log('Office Update Error:')
                    console.error(res);
                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            {(documentlist != undefined && documentlist.length === 0) || documentlist === undefined ? (<> <span className="error-message font-size-text " style={{ marginTop: '0px', marginBottom: '0px', paddingRight: '5px' }}>No Documents Attached</span></>) : (<>
                <button className="model-edit-button-ml" title="Account Stage 2 Approval" onClick={handleShow}>
                    <CheckTick />
                </button>
            </>)}

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>

                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Account Finalize for "{i.project_short_name}"
                            </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formattedDateLong(i?.officerent_billing_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.project_short_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Owner Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.owner_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Days:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.days}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.officerent_billing_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.year}
                                </div>
                            </div>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Basic Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    TDS Percentage:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_percentage)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    TDS Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <ViewDocumnetsbilling documentlist={documentlist} />
                            </div>
                            <>
                                <div className="flex-column">
                                    <label
                                        htmlFor="settlement_transaction_id"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Final Approval Details (Transaction / Cheque no.)
                                    </label>
                                    <input
                                        id="settlement_transaction_id"
                                        name="settlement_transaction_id"
                                        placeholder="Final Approval Details (Transaction / Cheque no.)"
                                        onChange={handleInputChange}
                                        value={formData.settlement_transaction_id}
                                        className={`form-input ${errors.settlement_transaction_id
                                            ? "error"
                                            : inputState.settlement_transaction_id
                                                ? "success"
                                                : ""
                                            }`}
                                    />
                                    {errors.settlement_transaction_id && (
                                        <span className="error-message font-size-text ">{errors.settlement_transaction_id}</span>
                                    )}
                                </div>

                                <div className="flex-row">
                                    <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Account Transaction Proof :
                                    </label>
                                    <div className="flex-column" >
                                        <label
                                            htmlFor="settlement_bill"
                                            className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.settlement_bill
                                                    ? "error"
                                                    : inputState.settlement_bill
                                                        ? "success"
                                                        : ""
                                                }`}
                                        >
                                            <div className="svg-field form-input align-center">
                                                <Attachment />
                                            </div>
                                        </label>
                                        <input
                                            id="settlement_bill"
                                            name="settlement_bill"
                                            type="file"
                                            onChange={handleFileChange}
                                            onFocus={handleClear}
                                            className="file-inputs align-center"
                                            style={{ display: "none", position: "relative" }}
                                        />
                                        <br />
                                        <div className="flex-row justify-evenly">
                                            <div>
                                                {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    {formData.settlement_bill ?
                                        <div>
                                            <button className="model-button" onClick={handleClear}>Clear</button>
                                        </div>
                                        : null
                                    }
                                    {errors.settlement_bill && (
                                        <span className="error-message font-size-text ">{errors.settlement_bill}</span>
                                    )}
                                </div>
                            </>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-leave font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div>


                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};




const DynamicRequestOfficeRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [formData, setFormData] = useState({
        remarks: "",
    })

    //******************Valedation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            let updatedFormData = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_by: sessionStorage.getItem('sitemanagement_emp_id'),
                updated_at: new Date().toISOString(),
            };

            try {
                let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`,
                    updatedFormData,);

                if (res.status === 200) {
                    await getDynamicRequestData();
                    handleClose()
                } else {
                    console.error(res);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-delete-button" title={`Office Stage ${i.level} Rejection`} onClick={handleShow}>
                <CrossRed />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Office "{i?.officerent_billing_details?.owner_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i?.officerent_billing_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.officerent_billing_details?.project_short_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Rent Amount -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Tds -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_amt || 0)} ({i?.officerent_billing_details?.tds_percentage}%)
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Total Amount -
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}
                                </div>
                            </div>




                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Number's of days :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.officerent_billing_details?.days}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Rejection Reason
                                </label>
                                <textarea
                                    id="remarks"
                                    type="text"
                                    name="remarks"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.remarks}
                                    className={`form-input-textarea   font-weight400  font-size-subheading ${errors.remarks
                                        ? "error"
                                        : inputState.remarks
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message font-size-text ">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-delete  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};

const DynamicRequestOfficeEditApproval = ({ i, getDynamicRequestData, documentlist }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const [maxlimit, setMaxlimit] = useState('')
    const [maxlimittds, setMaxlimittds] = useState('');
    console.log()
    const handleShow = () => {
        setMaxlimittds(parseFloat(i?.officerent_billing_details?.tds_percentage).toFixed(2))
        setMaxlimit(parseFloat(i?.officerent_billing_details?.rent_amt).toFixed(2));
        setFormData(i?.officerent_billing_details)
        setShow(true)
    };

    const [formData, setFormData] = useState({
        rent_amt: '',
        tds_percentage: '',
        tds_amt: '',
        total_amt: '',
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['rent_amt', 'total_amt']
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedData = { ...formData, [name]: value };

        if (name === "rent_amt") {
            // const maxLimit = parseFloat(formData.rent_amt).toFixed(2);
            console.log(maxlimit)
            const rentAmount = parseFloat(value).toFixed(2);
            const { success, error } = inputMaxLimit(name, rentAmount, maxlimit);
            if (!success) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: error,
                }));
                return updatedData;
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
                if (rentAmount >= 20000) {
                    const tdsPercentage = 10;
                    const tdsAmount = parseFloat(((rentAmount * tdsPercentage) / 100).toFixed(2));
                    updatedData = {
                        ...updatedData,
                        tds_percentage: tdsPercentage,
                        tds_amt: tdsAmount,
                        total_amt: parseFloat((parseFloat(rentAmount) - tdsAmount).toFixed(2)),
                    };
                } else {
                    updatedData = {
                        ...updatedData,
                        tds_percentage: "",
                        tds_amt: 0,
                        total_amt: parseFloat(rentAmount),
                    };
                }
            }

        }

        if (name === "tds_percentage" && Number(formData.rent_amt) >= 20000) {
            const tdsPercentage = parseFloat(value).toFixed(2);
            const { success, error } = inputMaxLimit(name, tdsPercentage, maxlimittds);
            if (!success) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: error,
                }));
                return updatedData;
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
                const tdsAmount = parseFloat(((Number(formData.rent_amt) * tdsPercentage) / 100).toFixed(2));
                updatedData = {
                    ...updatedData,
                    tds_amt: tdsAmount,
                    total_amt: parseFloat((Number(formData.rent_amt) - tdsAmount).toFixed(2)),
                };
            }

        }

        setFormData(updatedData);

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();
        let OfficeFormData = {}


        OfficeFormData = {
            rent_amt: formData.rent_amt,
        };

        // const updatedFormData = {
        //     ...formData,
        //     checker_approval_status: 'approved',
        //     checker: sessionStorage.getItem('sitemanagement_emp_code'),
        //     checker_update_datetime: new Date().toISOString(),

        // };
        let ApprovalFormData = {
            ...formData,
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };


        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            try {
                // let res = await axios.put(`${BASE_URL}/siteproject/officerentbillingupdate/${i.id}/`, updatedFormData);
                let off = await axios.put(`${BASE_URL}/siteproject/officerentbillingupdate/${i.officerent_billing_details?.id}/`,
                    formData,
                );
                if (off.status === 200 || off.status === 201) {
                    let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData);

                    if (res.status === 200) {
                        await getDynamicRequestData();
                        setShow(false);
                    } else {
                        alert(res);
                    }
                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            {(documentlist != undefined && documentlist.length === 0) || documentlist === undefined ? (<> <span className="error-message font-size-text " style={{ marginTop: '0px', marginBottom: '0px', paddingRight: '5px' }}>No Documents Attached</span></>) : (<>
                <button className="model-partial-button" title={`Office Stage ${i.level} Editable Approval`} onClick={handleShow}>
                    <WarnYellow />
                </button>
            </>)}


            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Checker Approval </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formattedDateLong(i?.officerent_billing_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.project_short_name}
                                </div>
                            </div>
                            {/* owner_name */}

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Owner Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.owner_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Days:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.days}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.officerent_billing_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.year}
                                </div>
                            </div>


                            <div title="Rent Amount" className="flex-column">
                                <label htmlFor="rent_amt" className="form-labels  font-weight500    font-size-subheading">
                                    Basic Amount<span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    max={i.rent_amt}
                                    maxLength={10}
                                    id="rent_amt"
                                    name="rent_amt"
                                    placeholder="Basic Amount."
                                    onChange={handleInputChange}
                                    value={formData.rent_amt}
                                    className={`form-input ${errors.rent_amt
                                        ? "error"
                                        : inputState.rent_amt
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.rent_amt && (
                                    <span className="error-message font-size-text ">{errors.rent_amt}</span>
                                )}
                            </div>
                            {Number(formData.rent_amt) < 20000
                                ? null :
                                <>
                                    <div title="TDS Percentage" className="flex-column">
                                        <label htmlFor="tds_percentage" className="form-labels  font-weight500    font-size-subheading">
                                            TDS Percentage<span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            maxLength={10}
                                            id="tds_percentage"
                                            name="tds_percentage"
                                            placeholder="TDS Percentage"
                                            onChange={handleInputChange}
                                            value={formData.tds_percentage}
                                            className={`form-input ${errors.tds_percentage
                                                ? "error"
                                                : inputState.tds_percentage
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.tds_percentage && (
                                            <span className="error-message font-size-text ">{errors.tds_percentage}</span>
                                        )}
                                    </div>
                                    <div title="TDS Amount" className="flex-column">
                                        <label htmlFor="tds_amt" className="form-labels  font-weight500    font-size-subheading">
                                            TDS Amount<span className="required">(Read Only)</span>
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            maxLength={10}
                                            id="tds_amt"
                                            name="tds_amt"
                                            placeholder="TDS Amount."
                                            // onChange={handleInputChange}
                                            value={formData.tds_amt}
                                            disabled
                                            readOnly
                                            style={{ cursor: 'not-allowed' }}
                                            className={`form-input ${errors.tds_amt
                                                ? "error"
                                                : inputState.tds_amt
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.tds_amt && (
                                            <span className="error-message font-size-text ">{errors.tds_amt}</span>
                                        )}
                                    </div>
                                </>
                            }
                            <div title="Total Amount" className="flex-column">
                                <label htmlFor="total_amt" className="form-labels  font-weight500    font-size-subheading">
                                    Total Amount<span className="required">(Auto Calculated)</span>
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    maxLength={10}
                                    id="total_amt"
                                    name="total_amt"
                                    placeholder="Total Amount"
                                    // onChange={handleInputChange}
                                    value={formData.total_amt}
                                    disabled
                                    readOnly
                                    style={{ cursor: 'not-allowed' }}
                                    className={`form-input ${errors.total_amt
                                        ? "error"
                                        : inputState.total_amt
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.total_amt && (
                                    <span className="error-message font-size-text ">{errors.total_amt}</span>
                                )}
                            </div>
                            <div className="flex-row">
                                <ViewDocumnetsbilling documentlist={documentlist} />
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div>
                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};
const DynamicRequestVehicleApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        let ApprovalFormData = {
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };

        try {
            let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`,
                ApprovalFormData,
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Vehicle Request Status Updated Successfully");
                await getDynamicRequestData();
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button className="model-edit-button-ml" title={`Vehicle Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Vehicle "{i.imprest_details?.imprest_name}" </h6>
                            {/* <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.imprest_details?.date)}
                                </div>
                            </div> */}

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.vehicle_billing_details?.vehicle_details?.project_short_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.date ? formatDate(i?.vehicle_billing_details?.vehicle_details?.date) : "-"}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.vehicle_billing_details?.vehicle_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.year}
                                </div>
                            </div>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Number:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.fixed_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.extra_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.total_km}
                                </div>
                            </div>




                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Bill Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Amount Per KM <br /> After Fixed KM :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Charges:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.total_amt)}
                                </div>
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};

const DynamicRequestVehicleRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [formData, setFormData] = useState({
        remarks: "",
    })

    //******************Valedation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            let updatedFormData = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_by: sessionStorage.getItem('sitemanagement_emp_id'),
                updated_at: new Date().toISOString(),
            };


            try {
                let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`,
                    updatedFormData,
                );

                if (res.status === 200) {
                    await getDynamicRequestData();
                    handleClose()
                } else {
                    console.error(res);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-delete-button" title={`Office Stage ${i.level} Rejection`} onClick={handleShow}>
                <CrossRed />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div>
                            Reject Billing  of Vehicle "{i?.vehicle_billing_details?.vehicle_details?.name}"
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.vehicle_billing_details?.vehicle_details?.project_short_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.date ? formatDate(i?.vehicle_billing_details?.vehicle_details?.date) : "-"}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.vehicle_billing_details?.vehicle_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.year}
                                </div>
                            </div>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Number:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.fixed_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.extra_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.total_km}
                                </div>
                            </div>




                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Bill Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Amount Per KM <br /> After Fixed KM :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Charges:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.total_amt)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Rejection Reason
                                </label>
                                <textarea
                                    id="remarks"
                                    type="text"
                                    name="remarks"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.remarks}
                                    className={`form-input-textarea   font-weight400  font-size-subheading ${errors.remarks
                                        ? "error"
                                        : inputState.remarks
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message font-size-text ">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-delete  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};

const DynamicVehicleFinal = ({ i, getVehicleBillingData, documentlist }) => {
    console.log(i, 'i vehicle final')
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        settlement: "",
        settlement_bill: "",
    });


    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "settlement_bill"
        ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            settlement_bill: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();
        const formDataa = new FormData();
        const symbols = '!@#$%^&*()_-+=';
        const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const now = new Date();
        const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
        const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
        const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * allChars.length);
            randomCode += allChars[randomIndex];
        }
        if (formData.settlement_bill) {
            const originalFile = formData?.settlement_bill;
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
            const customFile = new File([originalFile], customFileName, { type: originalFile.type });

            formDataa.append("settlement_bill", customFile);
        }

        formDataa.append("settlement_transaction_id", formData.settlement_transaction_id);


        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            try {
                let res = await axios.put(`${BASE_URL}/siteproject/vehiclerentbillingupdate/${i?.vehicle_billing_details
                    ?.id}/`, formDataa, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (res.status === 200) {
                    await getVehicleBillingData();
                    setShow(false);
                } else {
                    alert(res);
                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            {/* {(documentlist != undefined && documentlist.length === 0) || documentlist === undefined ? (<> <span className="error-message font-size-text " style={{ marginTop: '0px', marginBottom: '0px', paddingRight: '5px' }}>No Documents Attached</span></>) : (<> */}
            <button className="model-edit-button-ml" title="Account Stage 2 Approval" onClick={handleShow}>
                <CheckTick />
            </button>
            {/* </>)} */}

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Approval of Vehicle "{i?.vehicle_billing_details?.vehicle_details?.name}" </h6>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.vehicle_billing_details?.project__short_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bill Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.date ? formatDate(i?.vehicle_billing_details?.date) : "-"}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Period :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i.vehicle_billing_details?.month)} / {i?.vehicle_billing_details?.year}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Details:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_details?.name} / {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.fixed_km ? i.vehicle_billing_details?.fixed_km : i?.vehicle_billing_details?.vehicle_details?.fixed_km} km
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km} km
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.total_km === null
                                        ? Number(i?.vehicle_billing_details?.extra_km) + Number(i?.vehicle_billing_details?.vehicle_details?.fixed_km)
                                        : i?.vehicle_billing_details?.total_km}km

                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Amount Per KM Above Fixed KM:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    ₹{i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km}
                                </div>
                            </div>
                            {/* <hr /> */}
                            {/* <div className="flex-column">
                                <label htmlFor='fixed_bill_amt' className="form-labels  font-weight500 font-size-subheading" title="Fixed Amount">Fixed Amount<span className="required">*</span></label>
                                <input
                                    id='fixed_bill_amt'
                                    type="number"
                                    title="Fixed Amount"
                                    min={0}
                                    name="fixed_bill_amt"
                                    placeholder="Fixed Bill Amt"
                                    value={formData?.fixed_bill_amt}
                                    disabled
                                    onChange={handleInputChange}
                                    className={`form-input form-input-time ${errors?.fixed_bill_amt ? 'error' : ''}`}
                                />
                                {errors?.fixed_bill_amt && <div className="error-message font-size-text">{errors?.fixed_bill_amt}</div>}
                            </div>


                            <div className="flex-column">
                                <label htmlFor='extra_amount' className="form-labels  font-weight500 font-size-subheading" title="Extra Amount">Extra Amount<span className="required">*</span></label>
                                <input
                                    id='extra_amount'
                                    type="number"
                                    title="Extra Amount"
                                    min={0}
                                    name="extra_amount"
                                    placeholder="Extra Amount"
                                    value={formData?.extra_amount}
                                    disabled
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.extra_amount ? 'error' : ''}`}
                                />
                                {errors.extra_amount && <div className="error-message font-size-text">{errors.extra_amount}</div>}
                            </div>



                            <div className="flex-column">
                                <label htmlFor='final_total_amount' className="form-labels  font-weight500 font-size-subheading" title="Final Total Amount">Final Total Amount <span className="required">*</span></label>
                                <input
                                    id='final_total_amount'
                                    type="number"
                                    title="Final Total Amount"
                                    min={0}
                                    name="final_total_amount"
                                    placeholder="Final Total Amount"
                                    value={formData?.final_total_amount}
                                    onChange={handleInputChange}
                                    disabled
                                    className={`form-input ${errors.final_total_amount ? 'error' : ''}`}
                                />
                                {errors.final_total_amount && <div className="error-message font-size-text">{errors.final_total_amount}</div>}
                            </div> */}
                            <div className="flex-row">
                                <ViewDocumnetsbilling documentlist={documentlist} />
                            </div>

                            <>

                                <div className="flex-column">
                                    <label
                                        htmlFor="settlement_transaction_id"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Final Approval Details (Transaction / Cheque no.)
                                    </label>
                                    <input
                                        id="settlement_transaction_id"
                                        name="settlement_transaction_id"
                                        placeholder="Final Approval Details (Transaction / Cheque no.)"
                                        onChange={handleInputChange}
                                        value={formData.settlement_transaction_id}
                                        className={`form-input ${errors.settlement_transaction_id
                                            ? "error"
                                            : inputState.settlement_transaction_id
                                                ? "success"
                                                : ""
                                            }`}
                                    />
                                    {errors.settlement_transaction_id && (
                                        <span className="error-message font-size-text ">{errors.settlement_transaction_id}</span>
                                    )}
                                </div>

                                <div className="flex-row">
                                    <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Account Transaction Proof :
                                    </label>
                                    <div className="flex-column" >
                                        <label
                                            htmlFor="settlement_bill"
                                            className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.settlement_bill
                                                    ? "error"
                                                    : inputState.settlement_bill
                                                        ? "success"
                                                        : ""
                                                }`}
                                        >
                                            <div className="svg-field form-input align-center">
                                                <Attachment />
                                            </div>
                                        </label>
                                        <input
                                            id="settlement_bill"
                                            name="settlement_bill"
                                            type="file"
                                            onChange={handleFileChange}
                                            onFocus={handleClear}
                                            className="file-inputs align-center"
                                            style={{ display: "none", position: "relative" }}
                                        />
                                        <br />
                                        <div className="flex-row justify-evenly">
                                            <div>
                                                {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    {formData.settlement_bill ?
                                        <div>
                                            <button className="model-button" onClick={handleClear}>Clear</button>
                                        </div>
                                        : null
                                    }
                                    {errors.settlement_bill && (
                                        <span className="error-message font-size-text ">{errors.settlement_bill}</span>
                                    )}
                                </div>
                            </>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-leave font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div>


                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};



const DynamicRequestVehicleEditApproval = ({ i, getDynamicRequestData, documentlist }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const [maxlimit, setMaxlimit] = useState('')
    const [maxlimittds, setMaxlimittds] = useState('')
    const handleShow = () => {
        setShow(true)
        setFormData(i.vehicle_billing_details);
    };

    const [formData, setFormData] = useState({
        fixed_km: '',
        fixed_bill_amt: '',
        extra_amt_above_fix_km: '',
        extra_charges: '',
        extra_amount: "",
        extra_km: "",
        total_amount: "",
        settlement_bill: null
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['extra_km']
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };


    const fallbackFixedKm = i?.vehicle_billing_details?.fixed_km
        ?? i?.vehicle_billing_details?.vehicle_details?.fixed_km
        ?? 0;

    const fallbackFixedAmount = i?.vehicle_billing_details?.fixed_bill_amt
        ?? i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt
        ?? 0;


    const fix_km = Number(i.vehicle_billing_details?.fixed_km ? i.vehicle_billing_details?.fixed_km : fallbackFixedKm)


    const extra_amount = fix_km * Number(i.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km)

    const totalBillAMount = Number(i.vehicle_billing_details?.fixed_bill_amt ?? fallbackFixedAmount) + extra_amount;



    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const fallbackFixedKm = i?.vehicle_billing_details?.fixed_km
            ?? i?.vehicle_billing_details?.vehicle_details?.fixed_km
            ?? 0;

        const fallbackFixedAmount = i?.vehicle_billing_details?.fixed_bill_amt
            ?? i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt
            ?? 0;

        // Update field color
        setInputState((prev) => ({
            ...prev,
            [name]: value.trim() ? "green" : "",
        }));

        // Update form data
        const updatedFormData = {
            ...formData,
            [name]: value,
        };

        // If extra_km or fixed_km is being changed, recalculate total_km
        if (name === "extra_km" || name === "fixed_km") {
            const extra_km = name === "extra_km" ? Number(value) : Number(formData.extra_km || 0);
            const fixed_km = name === "fixed_km" ? Number(value) : Number(formData.fixed_km || fallbackFixedKm);
            const extra_amount_perKm = name === "extra_amt_above_fix_km" ? Number(value) : Number(formData.extra_amt_above_fix_km || 0);
            const fixed_bill_amt = name === "fixed_bill_amt" ? Number(value) : Number(formData.fixed_bill_amt || fallbackFixedAmount);

            const total_km = extra_km + fixed_km;
            updatedFormData.total_km = total_km;

            const extra_amount = fixed_km * extra_amount_perKm
            updatedFormData.extra_amount = extra_amount;

            const total_amount = fixed_bill_amt + extra_amount;
            updatedFormData.total_amount = total_amount;


            // Update total_km color too
            setInputState((prev) => ({
                ...prev,
                total_km: total_km ? "green" : "",
                extra_amount: extra_amount ? "green" : "",
                total_amount: total_amount ? "green" : "",
            }));

            // Remove total_km error if any
            if (errors.total_km) {
                setErrors((prevErrors) => {
                    const { total_km, extra_amount, total_amount, ...rest } = prevErrors;
                    return rest;
                });
            }
        }

        setFormData(updatedFormData);

        // Remove field error if any
        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };




    const handleFormSubmitPut = async (e) => {

        e.preventDefault();

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };


        if (validateForm()) {
            const { settlement_bill, ...dataToSubmit } = formData;
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            try {
                let res = await axios.put(`${BASE_URL}/siteproject/vehiclerentbillingupdate/${i?.vehicle_billing_details?.id}/`, dataToSubmit);

                if (res.status === 200 || res.data === 201) {
                    toast.success("Vehicle Billing Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);
                    if (req.status === 200 || req.status === 201) {
                        toast.success("Vehicle Billing Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Vehicle Billing Update Error: ${req.statusText}`);
                        console.log('Vehicle Billing Update Error:')
                        console.error(req);
                    }
                } else {
                    toast.error(`Vehicle Billing Error: ${res.statusText}`);
                    console.log('Vehicle Billing Error:')
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-partial-button" title={`Vehicle Stage ${i.level} Editable Approval`} onClick={handleShow}>
                <WarnYellow />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="three-fourth-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div className='text-center'>
                            Partial Approval of  "{i?.vehicle_billing_details?.vehicle_details?.name}"
                        </div>

                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="parent-div">

                        {/* <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Confirm Checker Approval  "{i.project_short_name}"
                            </h6>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i?.vehicle_billing_details?.project_short_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.date ? formatDate(i?.vehicle_billing_details?.date) : "-"}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.vehicle_billing_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.year}
                                </div>
                            </div>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Vehicle Number:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.vehicle_num}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.fixed_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.extra_km}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Km:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.vehicle_billing_details?.total_km}
                                </div>
                            </div>




                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Fixed Bill Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.fixed_bill_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Amount Per KM <br /> After Fixed KM :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.extra_amt_above_fix_km)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Extra Charges:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.vehicle_billing_details?.total_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <ViewDocumnetsbilling documentlist={documentlist} />
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-leave font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div> */}

                        <div className="bdy-div">
                            <form>


                                <div className="flex-row">
                                    <label
                                        htmlFor="project_name"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Project :
                                    </label>
                                    <div className="form-labels announce-date font-size-heading">
                                        {i?.vehicle_billing_details?.project__short_name}
                                    </div>
                                </div>
                                <div className="flex-row">
                                    <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                        Bill Date :
                                    </label>
                                    <div className="form-labels announce-date font-size-heading padding-lefttt">
                                        {i?.vehicle_billing_details?.date ? formatDate(i?.vehicle_billing_details?.date) : "-"}
                                    </div>
                                </div>

                                <div className="flex-row">
                                    <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                        Period :
                                    </label>
                                    <div className="form-labels announce-date font-size-heading padding-lefttt">
                                        {getMonthName(i.vehicle_billing_details?.month)} / {i?.vehicle_billing_details?.year}
                                    </div>
                                </div>
                                <div className="flex-row">
                                    <label
                                        htmlFor="project_name"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Vehicle Details:
                                    </label>
                                    <div className="form-labels announce-date font-size-heading padding-lefttt">
                                        {i?.vehicle_billing_details?.vehicle_details?.name} / {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}
                                    </div>
                                </div>
                                <div className="flex-row">
                                    <label
                                        htmlFor="project_name"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Fixed Km:
                                    </label>
                                    <div className="form-labels announce-date font-size-heading padding-lefttt">
                                        {i?.vehicle_billing_details?.fixed_km ? i.vehicle_billing_details?.fixed_km : i?.vehicle_billing_details?.vehicle_details?.fixed_km} km
                                    </div>
                                </div>

                                <div className="flex-row">
                                    <label
                                        htmlFor="project_name"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Extra Amount Per KM Above Fixed KM:
                                    </label>
                                    <div className="form-labels announce-date font-size-heading padding-lefttt">
                                        ₹{i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km}
                                    </div>
                                </div>
                                <hr />
                                <div className='flex-row justify-between'>

                                    <div className="flex-column">
                                        <label htmlFor='fixed_km' className="form-labels  font-weight500 font-size-subheading" title="Fixed KM">Fixed KM<span className="required">*</span></label>
                                        <input
                                            id='fixed_km'
                                            type="number"
                                            title="Fixed KM"
                                            min={0}
                                            max={i?.vehicle_billing_details?.fixed_km === null ? 0 : i?.vehicle_billing_details?.fixed_km}
                                            name="fixed_km"
                                            placeholder={i?.vehicle_billing_details?.fixed_km === null ? i?.vehicle_billing_details?.vehicle_details.fixed_km : i?.vehicle_billing_details?.fixed_km}
                                            value={formData?.fixed_km}
                                            onChange={handleInputChange}
                                            disabled
                                            className={`form-input form-input-time ${errors?.fixed_km ? 'error' : ''}`}
                                        />
                                        {errors?.fixed_km && <div className="error-message font-size-text">{errors?.fixed_km}</div>}
                                    </div>
                                    <div className="flex-column">
                                        <label htmlFor='extra_km' className="form-labels  font-weight500 font-size-subheading" title="Extra KM">Extra KM<span className="required">*</span></label>
                                        <input
                                            id='extra_km'
                                            type="number"
                                            title="Extra KM"
                                            min={0}
                                            max={i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km}
                                            name="extra_km"
                                            placeholder={i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km}
                                            value={formData?.extra_km}
                                            onChange={handleInputChange}
                                            className={`form-input form-input-time ${errors?.extra_km ? 'error' : ''}`}
                                        />
                                        {errors?.extra_km && <div className="error-message font-size-text">{errors?.extra_km}</div>}
                                    </div>
                                    <div className="flex-column">
                                        <label htmlFor='total_km' className="form-labels  font-weight500 font-size-subheading" title="Total KM">Total KM<span className="required">*</span></label>
                                        <input
                                            id='total_km'
                                            type="number"
                                            title="Total KM"
                                            min={0}
                                            max={i?.vehicle_billing_details?.total_km ? i.vehicle_billing_details?.total_km : i?.vehicle_billing_details?.vehicle_details?.total_km}
                                            name="total_km"
                                            placeholder={
                                                i.vehicle_billing_details
                                                    ? i.vehicle_billing_details.total_km === null
                                                        ? Number(i.vehicle_billing_details.extra_km) + Number(i.vehicle_billing_details.vehicle_details?.fixed_km || 0)
                                                        : Number(i.vehicle_billing_details.total_km)
                                                    : ''
                                            }

                                            value={formData?.total_km}
                                            onChange={handleInputChange}
                                            disabled
                                            className={`form-input form-input-time ${errors?.total_km ? 'error' : ''}`}
                                        />
                                        {errors?.total_km && <div className="error-message font-size-text">{errors?.total_km}</div>}
                                    </div>
                                </div>
                                <div className='form-flex-wrap'>
                                    <div className="flex-column">
                                        <label htmlFor='fixed_bill_amt' className="form-labels  font-weight500 font-size-subheading" title="Fixed Amount">Fixed Amount<span className="required">*</span></label>
                                        <input
                                            id='fixed_bill_amt'
                                            type="number"
                                            title="Fixed Amount"
                                            min={0}
                                            max={i?.vehicle_billing_details?.fixed_bill_amt ? i.vehicle_billing_details?.fixed_bill_amt : i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt}
                                            name="fixed_bill_amt"
                                            placeholder={i?.vehicle_billing_details?.fixed_bill_amt ? i.vehicle_billing_details?.fixed_bill_amt : i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt}
                                            value={formData?.fixed_bill_amt}
                                            onChange={handleInputChange}
                                            className={`form-input form-input-time ${errors?.fixed_bill_amt ? 'error' : ''}`}
                                            disabled
                                        />
                                        {errors?.fixed_bill_amt && <div className="error-message font-size-text">{errors?.fixed_bill_amt}</div>}
                                    </div>

                                    <div className="flex-column">
                                        <label htmlFor='extra_amount' className="form-labels  font-weight500 font-size-subheading" title="Extra Amount">Extra Amount<span className="required">*</span></label>
                                        <input
                                            id='extra_amount'
                                            type="number"
                                            title="Extra Amount"
                                            min={0}
                                            name="extra_amount"
                                            placeholder="Extra Amount"
                                            value={formData?.extra_amount}
                                            disabled
                                            // onChange={handleInputChange}
                                            className={`form-input form-input-time ${errors.extra_amount ? 'error' : ''}`}
                                        />
                                        {errors.extra_amount && <div className="error-message font-size-text">{errors.extra_amount}</div>}
                                    </div>

                                    <div className="flex-column">
                                        <label htmlFor='total_amount' className="form-labels  font-weight500 font-size-subheading" title="Final Total Amount">Final Total Amount <span className="required">*</span></label>
                                        <input
                                            id='total_amount'
                                            type="number"
                                            title="Final Total Amount"
                                            min={0}
                                            name="total_amount"
                                            placeholder={totalBillAMount}
                                            value={formData?.total_amount}
                                            // onChange={handleInputChange}
                                            disabled
                                            className={`form-input form-input-time ${errors.total_amount ? 'error' : ''}`}
                                        />
                                        {errors.total_amount && <div className="error-message font-size-text">{errors.total_amount}</div>}
                                    </div>
                                </div>
                                <div className="button-models">
                                    <button className="model-button-cancel model-button font-weight500" onClick={(e) => handleClose(e)}>
                                        Cancel
                                    </button>
                                    <button className="model-button  font-weight500    model-button-leave font-size-heading" onClick={handleFormSubmitPut}>
                                        {/* {loading ? 'Adding...' : 'Add'} */}Add
                                    </button>
                                </div>
                                {/* <p className="error-message font-size-text">
                                    {isSubmitted && Object.keys(errors).length > 0 && (
                                        Object.keys(errors).length > 5 ? (
                                            <h5 className="text-center">Please fill all mandatory fields!</h5>
                                        ) : (

                                            <h6 className="text-center">
                                                {Object.keys(errors).map((field) =>
                                                    field.charAt(0).toUpperCase() + field.slice(1)
                                                ).join(', ') + ' are required!'}
                                            </h6>
                                        )
                                    )}
                                </p> */}
                            </form>
                        </div>


                    </div>

                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};

const DynamicRequestTenderApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
    });

    // console.log("formData- ", formData)
    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        let TenderFormData = {}

        TenderFormData = {
            approved_amount: formData.amount,
        };

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        // console.log("TenderFormData")
        // console.log(TenderFormData)
        // console.log("ApprovalFormData")
        // console.log(ApprovalFormData)

        try {
            let tender = await axios.put(`${BASE_URL}/siteproject/tenderRequestAmount/${i.tender_amount_details?.id}/`,
                TenderFormData,
            );

            if (tender.status === 200 || tender.status === 201) {
                toast.success("Tender Updated Successfully");
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`,
                    ApprovalFormData,
                );
                if (req.status === 200 || req.status === 201) {
                    toast.success("Tender Request Status Updated Successfully");
                    await getDynamicRequestData();
                } else {
                    toast.error(`Tender Request Update Error: ${req.statusText}`);
                    console.log('Tender Request Update Error:')
                    console.error(req);
                }
            } else {
                toast.error(`Tender Update Error: ${tender.statusText}`);
                console.log('Tender Update Error:')
                console.error(tender);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };
    console.log("i-", i)

    return (
        <>
            <button className="model-edit-button-ml" title={`Tender Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Imprest "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">

                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestTenderEditApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
            amount_change_reason: "",
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
        amount_change_reason: "",
    });

    //******************Validation Start********************* */
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["amount_change_reason"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // const handleInputChange = (e) => {
    //     const { name, value } = e.target;

    //     if (name.startsWith('approved_')) {

    //         // For individual expenses like transportation, accommodation, fooding, etc.
    //         if (!name.startsWith('approved_other_')) {
    //             const itemAmountKey = name.replace('approved_', '');
    //             const currentValue = i[itemAmountKey] !== undefined && i[itemAmountKey] !== null ? i[itemAmountKey] : 0;
    //             const maxLength = getNumberLength(currentValue);

    //             // Ensure the input value does not exceed the allowed length
    //             if (value.length <= maxLength) {
    //                 const numericValue = value.trim() ? parseInt(value, 10) : 0;

    //                 // Check if the numeric value is less than or equal to the current value
    //                 if (numericValue <= currentValue) {
    //                     setInputState({
    //                         ...inputState,
    //                         [name]: value.trim() ? "success" : "",
    //                     });

    //                     setFormData({
    //                         ...formData,
    //                         [name]: value,
    //                     });

    //                     // Clear any previous error
    //                     setErrors({
    //                         ...errors,
    //                         [name]: "",
    //                     });
    //                 } else {
    //                     // Handle case where the numeric value exceeds the current value
    //                     setErrors({
    //                         ...errors,
    //                         [name]: `Value must be less than or equal to ${currentValue}`,
    //                     });
    //                 }
    //             } else {
    //                 // Handle case where input length exceeds the allowed length
    //                 setErrors({
    //                     ...errors,
    //                     [name]: `Maximum allowed length is ${maxLength} digits`,
    //                 });
    //             }

    //         } else {
    //             // For entries in approved_other_details
    //             const index = parseInt(name.replace('approved_other_', ''), 10);
    //             const currentOther = i.imprest_details?.other_details[index];
    //             const currentValue = currentOther.amount !== undefined && currentOther.amount !== null ? currentOther.amount : 0;
    //             const maxLength = getNumberLength(currentValue);

    //             // Ensure the input value does not exceed the allowed length
    //             if (value.length <= maxLength) {
    //                 const numericValue = value.trim() ? parseInt(value, 10) : 0;

    //                 // Check if the numeric value is less than or equal to the current value
    //                 if (numericValue <= currentValue) {
    //                     const newOtherDetails = [...formData.other_details];
    //                     newOtherDetails[index].approved_other = value;

    //                     setInputState({
    //                         ...inputState,
    //                         [name]: value.trim() ? "success" : "",
    //                     });

    //                     setFormData({
    //                         ...formData,
    //                         other_details: newOtherDetails,
    //                     });

    //                     // Clear any previous error
    //                     setErrors({
    //                         ...errors,
    //                         [name]: "",
    //                     });
    //                 } else {
    //                     // Handle case where the numeric value exceeds the current value
    //                     setErrors({
    //                         ...errors,
    //                         [name]: `Value must be less than or equal to ${currentValue}`,
    //                     });
    //                 }
    //             } else {
    //                 // Handle case where input length exceeds the allowed length
    //                 setErrors({
    //                     ...errors,
    //                     [name]: `Maximum allowed length is ${maxLength} digits`,
    //                 });
    //             }
    //         }

    //     } else {
    //         // For textarea or other fields without length validation
    //         setInputState({
    //             ...inputState,
    //             [name]: value.trim() ? "success" : "",
    //         });

    //         setFormData({
    //             ...formData,
    //             [name]: value,
    //         });
    //         setErrors({
    //             ...errors,
    //             [name]: "",
    //         });
    //     }
    // };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "approved_amount") {
            const currentValue = i.tender_amount_details?.amount ?? 0;
            const maxLength = getNumberLength(currentValue);
            const numericValue = value.trim() ? parseInt(value, 10) : 0;

            if (value.length <= maxLength) {
                if (numericValue <= currentValue) {
                    setInputState({
                        ...inputState,
                        [name]: value.trim() ? "green" : "",
                    });

                    setFormData({
                        ...formData,
                        [name]: value,
                    });

                    setErrors({
                        ...errors,
                        [name]: "",
                    });
                } else {
                    setErrors({
                        ...errors,
                        [name]: `Value must be less than or equal to ${currentValue}`,
                    });
                }
            } else {
                setErrors({
                    ...errors,
                    [name]: `Maximum allowed length is ${maxLength} digits`,
                });
            }
        } else {
            // For fields like amount_change_reason
            setInputState({
                ...inputState,
                [name]: value.trim() ? "green" : "",
            });

            setFormData({
                ...formData,
                [name]: value,
            });

            setErrors({
                ...errors,
                [name]: "",
            });
        }
    };



    function getNumberLength(value) {
        return value?.toString()?.length;
    }

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait...");
            let TenderFormData = {}

            TenderFormData = {
                approved_amount: formData.amount,
                amount_change_reason: formData.amount_change_reason,
            };

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };


            try {
                let tender = await axios.put(`${BASE_URL}/siteproject/tenderRequestAmount/${i.tender_amount_details?.id}/`,
                    TenderFormData,
                );

                if (tender.status === 200 || tender.status === 201) {
                    toast.success("Tender Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Tender Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Tender Request Update Error: ${req.statusText}`);
                        console.log('Tender Request Update Error:')
                        console.error(req);
                    }
                } else {
                    toast.error(`Tender Update Error: ${tender.statusText}`);
                    console.log('Tender Update Error:')
                    console.error(tender);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId);
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-partial-button" title={`Tender Stage ${i.level} Editable Approval`} onClick={handleShow}>
                <WarnYellow />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h1 className="task-Tab-heading align-center font-weight500 font-size-heading">Give Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approvable Amount for Tender : "{i.imprest_details?.imprest_name}" </h1>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">

                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            <div title="Rent Amount" className="flex-column">
                                <label htmlFor="approved_amount" className="form-labels  font-weight500    font-size-subheading">
                                    Amount Approval<span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    max={i.amount}
                                    maxLength={10}
                                    id="approved_amount"
                                    name="approved_amount"
                                    placeholder="Basic Amount."
                                    onChange={handleInputChange}
                                    value={formData.approved_amount}
                                    className={`form-input ${errors.approved_amount
                                        ? "error"
                                        : inputState.approved_amount
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.approved_amount && (
                                    <span className="error-message font-size-text ">{errors.approved_amount}</span>
                                )}
                            </div>

                            <div className="flex-column">
                                <label htmlFor="amount_change_reason" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Amount Change Reason<span className="required">*</span>
                                </label>
                                <textarea
                                    id="amount_change_reason"
                                    type="text"
                                    name="amount_change_reason"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.amount_change_reason}
                                    className={`form-input-textarea font-weight400 font-size-subheading ${errors.amount_change_reason ? "error" : inputState.amount_change_reason ? "success" : ""}`}
                                />
                                {errors.amount_change_reason && (
                                    <span className="error-message font-size-text">{errors.amount_change_reason}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-submit font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal>
        </>
    );
};

const DynamicRequestTenderFinalize = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),

            account_approved_final_details: "",
            account_transaction_proof: null,
            imprest_open: true,
        })
        setShow(true)
    };


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),

        account_approved_final_details: "",
        account_transaction_proof: null,
        imprest_open: true,
    });

    const totalApproved =
        i.imprest_details?.change_log?.length > 1
            ?
            (Number(i.imprest_details?.approved_stationary_item) +
                Number(i.imprest_details?.approved_electronic_item) +
                Number(i.imprest_details?.approved_grocery) +
                // Number(i.approved_other)
                (i.imprest_details?.other_details?.length > 0
                    ? i.imprest_details?.other_details?.reduce((total, other) => total + Number(other.approved_other), 0)
                    : 0))
            : (
                Number(formData.approved_stationary_item) +
                Number(formData.approved_electronic_item) +
                Number(formData.approved_grocery) +
                (formData.other_details?.length > 0
                    ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                    : 0)
            );

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields =
            totalApproved === 0 ? [] :
                [
                    "account_approved_final_details",
                    "account_transaction_proof"
                ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            account_transaction_proof: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            const ImprestFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            if (formData.account_transaction_proof && formData.account_approved_final_details) {
                const originalFile = formData?.account_transaction_proof;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ImprestFormData.append("account_approved_final_details", formData.account_approved_final_details);
                ImprestFormData.append("account_transaction_proof", customFile);
            }
            ImprestFormData.append("imprest_open", formData.imprest_open);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`, ImprestFormData, AUTHH, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (imp.status === 200 || imp.status === 201) {
                    toast.success("Imprest Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                        console.log('Imprest Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                    console.log('Imprest Update Error:')
                    console.error(imp);
                }

                // if (imp.status === 200) {
                //     await getDynamicRequestData();
                //     setShow(false);
                // } else {
                //     alert(imp);
                // }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-edit-button-ml" title={`Tender Stage ${i.level} Finalize Approval`} onClick={handleShow}>
                <CheckTick />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Finalize Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Tender "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}

                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            {totalApproved == 0 ? null :
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="account_approved_final_details"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Final Approval Details (Transaction / Cheque no.)
                                        </label>
                                        <input
                                            id="account_approved_final_details"
                                            name="account_approved_final_details"
                                            placeholder="Final Approval Details (Transaction / Cheque no.)"
                                            onChange={handleInputChange}
                                            value={formData.account_approved_final_details}
                                            className={`form-input ${errors.account_approved_final_details
                                                ? "error"
                                                : inputState.account_approved_final_details
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.account_approved_final_details && (
                                            <span className="error-message font-size-text ">{errors.account_approved_final_details}</span>
                                        )}
                                    </div>

                                    <div className="flex-row">
                                        <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                            Account Transaction Proof :
                                        </label>
                                        <div className="flex-column" >
                                            <label
                                                htmlFor="account_transaction_proof"
                                                className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.account_transaction_proof
                                                        ? "error"
                                                        : inputState.account_transaction_proof
                                                            ? "success"
                                                            : ""
                                                    }`}
                                            >
                                                <div className="svg-field form-input align-center">
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                id="account_transaction_proof"
                                                name="account_transaction_proof"
                                                type="file"
                                                onChange={handleFileChange}
                                                onFocus={handleClear}
                                                className="file-inputs align-center"
                                                style={{ display: "none", position: "relative" }}
                                            />
                                            <br />
                                            <div className="flex-row justify-evenly">
                                                <div>
                                                    {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                                </div>
                                            </div>
                                        </div>
                                        {formData.account_transaction_proof ?
                                            <div>
                                                <button className="model-button" onClick={handleClear}>Clear</button>
                                            </div>
                                            : null
                                        }
                                        {errors.account_transaction_proof && (
                                            <span className="error-message font-size-text ">{errors.account_transaction_proof}</span>
                                        )}
                                    </div>
                                </>
                            }
                            <br />

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestTenderRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            remarks: "",
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        remarks: "",
    })

    //******************Valedation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            let RejectedStatus = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {

                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, RejectedStatus);

                if (req.status === 200 || req.status === 201) {
                    toast.success("Imprest Request Status Updated Successfully");
                    await getDynamicRequestData();
                } else {
                    toast.error(`Imprest Request Update Error: ${req.statusText}`);
                    console.log('Imprest Request Update Error:')
                    console.error(req);

                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-delete-button" title={`Tender Stage ${i.level} Rejection`} onClick={handleShow}>
                <CrossRed />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Rejection of Tender "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">

                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Rejection Reason
                                </label>
                                <textarea
                                    id="remarks"
                                    type="text"
                                    name="remarks"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.remarks}
                                    className={`form-input-textarea   font-weight400  font-size-subheading ${errors.remarks
                                        ? "error"
                                        : inputState.remarks
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message font-size-text ">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-delete  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal >
        </>
    );
};


const DynamicRequestBankDetailsApproval = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitPut = async () => {
        const loadingToastId = toast.loading("Approving... Please wait...");
        const ApprovalFormData = {
            approval_status: "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapprovals/${i.id}/`,
                ApprovalFormData
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Bank Details Request Approved");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Approval Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-edit-button-ml"
                title={`Bank Change Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CheckTick />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Confirm Stage {i.level} : {i.role} Approval for{" "}<br />
                                {i?.bank_details?.emp_code}-{i?.bank_details?.emp_name}'s Bank Details Change
                            </h6>

                            <div className='flex-row justify-evenly'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Current Details:</h6>
                                    <div className="mb-4">
                                        <table >
                                            <tbody>
                                                {[
                                                    { label: "Account No", value: i?.bank_details?.previous_data?.account_number },
                                                    { label: "IFSC", value: i?.bank_details?.previous_data?.ifsc_code },
                                                    { label: "Bank", value: i?.bank_details?.previous_data?.bank_name },
                                                    { label: "Branch", value: i?.bank_details?.previous_data?.branch_address }
                                                ].map(({ label, value }) => (
                                                    <tr key={label}>
                                                        <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                        <td style={{ padding: "4px 8px" }}>:</td>
                                                        <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Requested Updates:</h6>
                                    <div className="mb-4">
                                        <table >
                                            <tbody>
                                                {[
                                                    { label: "Account No", value: i?.bank_details?.updated_data?.account_number },
                                                    { label: "IFSC", value: i?.bank_details?.updated_data?.ifsc_code },
                                                    { label: "Bank", value: i?.bank_details?.updated_data?.bank_name },
                                                    { label: "Branch", value: i?.bank_details?.updated_data?.branch_address }
                                                ].map(({ label, value }) => (
                                                    <tr key={label}>
                                                        <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                        <td style={{ padding: "4px 8px" }}>:</td>
                                                        <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <hr />

                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Changed Fields:</h6>
                                    <ul>
                                        {i?.bank_details?.changed_fields?.length > 0 ? (
                                            i.bank_details.changed_fields.map((f, idx) => (
                                                <li key={idx}>{f}</li>
                                            ))
                                        ) : (
                                            <li>-</li>
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                                    {i?.bank_details.proof_attachment ? (
                                        <ViewFile
                                            doc={i?.bank_details.proof_attachment}
                                            filename={"Proof Attachment"}
                                            filesrc={i?.bank_details.proof_attachment}
                                        />
                                    ) : (
                                        'No File Attached'
                                    )}
                                </div>
                            </div>
                            <div className='flex-row emp_email_u_body'>
                                <h6 className="font-weight500 mt-2">Reason:</h6>
                                <p className=''>{i?.bank_details?.reason_for_change}</p>
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-leave font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestBankDetailsEditApproval = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [formData, setFormData] = useState({
        account_number: "",
        ifsc_code: "",
        bank_name: "",
        branch_address: "",
        remarks: ""
    });
    const [errors, setErrors] = useState({});

    const handleClose = () => setShow(false);

    const handleShow = () => {
        setFormData({
            account_number: i?.bank_details?.updated_data?.account_number || "",
            ifsc_code: i?.bank_details?.updated_data?.ifsc_code || "",
            bank_name: i?.bank_details?.updated_data?.bank_name || "",
            branch_address: i?.bank_details?.updated_data?.branch_address || "",
            remarks: ""
        });
        setErrors({});
        setShow(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        ["account_number", "ifsc_code", "bank_name", "branch_address", "remarks"].forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${field.replace(/_/g, " ")} is required!`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmitPut = async () => {
        if (!validateForm()) return;
        const loadingToastId = toast.loading("Submitting approval...");

        // compute changed fields against previous_data
        const prev = i?.bank_details?.previous_data || {};
        const changedFields = Object.keys(formData).filter(
            (key) =>
                key !== "remarks" &&
                formData[key] !== prev[key]
        );

        const ApprovalFormData = {
            approval_status: "approved",
            remarks: formData.remarks,
            updated_data: {
                account_number: formData.account_number,
                ifsc_code: formData.ifsc_code,
                bank_name: formData.bank_name,
                branch_address: formData.branch_address,
            },
            changed_fields: changedFields,
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-approve-reject-edit-bankdetails/${i.id}/`,
                ApprovalFormData
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Bank Details Request Approved with Changes");
                await getDynamicRequestData();
                await getApprovalStages();
                handleClose();
            } else {
                toast.error(`Approval Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button
                className="model-partial-button"
                title={`Stage ${i.level} Editable Approval`}
                onClick={handleShow}
            >
                <WarnYellow />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Edit & Approve Stage {i.level} : {i.role} Approval
                            </h6>

                            {/* Current Details */}
                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Current Details:</h6>
                                    <table>
                                        <tbody>
                                            {[
                                                { label: "Account No", value: i?.bank_details?.previous_data?.account_number },
                                                { label: "IFSC", value: i?.bank_details?.previous_data?.ifsc_code },
                                                { label: "Bank", value: i?.bank_details?.previous_data?.bank_name },
                                                { label: "Branch", value: i?.bank_details?.previous_data?.branch_address }
                                            ].map(({ label, value }) => (
                                                <tr key={label}>
                                                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                    <td style={{ padding: "4px 8px" }}>:</td>
                                                    <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                                    {i?.bank_details.proof_attachment ? (
                                        <ViewFile
                                            doc={i?.bank_details.proof_attachment}
                                            filename={"Proof Attachment"}
                                            filesrc={i?.bank_details.proof_attachment}
                                        />
                                    ) : (
                                        'No File Attached'
                                    )}
                                </div>

                            </div>
                            <div className='flex-row emp_email_u_body'>
                                <h6 className="font-weight500 mt-2">Reason:</h6>
                                <p className=''>{i?.bank_details?.reason_for_change || '-'}</p>
                            </div>
                            <hr />

                            {/* Editable Updates */}
                            <div>
                                <h6 className="font-weight500 mt-2">Edit Requested Updates:</h6>
                                {["account_number", "ifsc_code", "bank_name", "branch_address"].map((field) => (
                                    <div key={field} className="flex-column mb-2">
                                        <label className="form-labels font-weight500">
                                            {field.replace(/_/g, " ").toUpperCase()}
                                        </label>
                                        <input
                                            name={field}
                                            value={formData[field]}
                                            onChange={handleInputChange}
                                            className={`form-input ${errors[field] ? "error" : ""}`}
                                        />
                                        {errors[field] && (
                                            <span className="error-message">{errors[field]}</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Remarks */}
                            <div className="flex-column mt-3">
                                <label className="form-labels font-weight500">
                                    Remarks <span className="required">*</span>
                                </label>
                                <textarea
                                    rows="3"
                                    name='remarks'
                                    placeholder="Enter remarks"
                                    onChange={handleInputChange}
                                    value={formData.remarks}
                                    className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                />
                                {errors.remarks && (
                                    <span className="error-message">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-submit"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve with Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestBankDetailsFinalize = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        account_approved_final_details: "",
        account_transaction_proof: null,
    });
    const [errors, setErrors] = useState({});

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            account_approved_final_details: "",
            account_transaction_proof: null,
        });
        setFileName("");
        setErrors({});
        setShow(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, account_transaction_proof: file });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const validateForm = () => {
        const newErrors = {};
        // if (!formData.account_approved_final_details) {
        //     newErrors.account_approved_final_details = "Final details are required!";
        // }
        // if (!formData.account_transaction_proof) {
        //     newErrors.account_transaction_proof = "Transaction proof is required!";
        // }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmitPut = async () => {
        if (!validateForm()) return;

        const loadingToastId = toast.loading("Finalizing Bank Details...");
        const ApprovalFormData = {
            approval_status: "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            const req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-finalize-bankdetails/${i.id}/`,
                ApprovalFormData,
            );

            if (req.status === 200 || req.status === 201) {
                toast.success("Bank Details Finalized Successfully");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Finalize Error: ${req.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-edit-button-ml"
                title={`Finalize Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CheckTick />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Finalize Stage {i.level} : {i.role} Approval for <br />
                                {i?.bank_details?.emp_code}-{i?.bank_details?.emp_name}'s Bank Details Change
                            </h6>

                            <div className="flex-row justify-evenly">
                                <div>
                                    <h6 className="font-weight500 mt-2">Current Details:</h6>
                                    <table>
                                        <tbody>
                                            {[
                                                { label: "Account No", value: i?.bank_details?.previous_data?.account_number },
                                                { label: "IFSC", value: i?.bank_details?.previous_data?.ifsc_code },
                                                { label: "Bank", value: i?.bank_details?.previous_data?.bank_name },
                                                { label: "Branch", value: i?.bank_details?.previous_data?.branch_address }
                                            ].map(({ label, value }) => (
                                                <tr key={label}>
                                                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                    <td style={{ padding: "4px 8px" }}>:</td>
                                                    <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Requested Updates:</h6>
                                    <table>
                                        <tbody>
                                            {[
                                                { label: "Account No", value: i?.bank_details?.updated_data?.account_number },
                                                { label: "IFSC", value: i?.bank_details?.updated_data?.ifsc_code },
                                                { label: "Bank", value: i?.bank_details?.updated_data?.bank_name },
                                                { label: "Branch", value: i?.bank_details?.updated_data?.branch_address }
                                            ].map(({ label, value }) => (
                                                <tr key={label}>
                                                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                    <td style={{ padding: "4px 8px" }}>:</td>
                                                    <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <hr />

                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Changed Fields:</h6>
                                    <ul>
                                        {i?.bank_details?.changed_fields?.length > 0 ? (
                                            i.bank_details.changed_fields.map((f, idx) => (
                                                <li key={idx}>{f}</li>
                                            ))
                                        ) : (
                                            <li>-</li>
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                                    {i?.bank_details?.proof_attachment ? (
                                        <ViewFile
                                            doc={i?.bank_details?.proof_attachment}
                                            filename={"Proof Attachment"}
                                            filesrc={i?.bank_details?.proof_attachment}
                                        />
                                    ) : (
                                        'No File Attached'
                                    )}
                                </div>
                            </div>
                            <div className='flex-row emp_email_u_body'>
                                <h6 className="font-weight500 mt-2">Reason:</h6>
                                <p className=''>{i?.bank_details?.reason_for_change}</p>
                            </div>

                            <div className="button-models mt-4">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="model-button model-button-leave font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Finalize
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestBankDetailsRejection = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [formData, setFormData] = useState({ remarks: "" });
    const [errors, setErrors] = useState({});

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({ remarks: "" });
        setErrors({});
        setShow(true);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.remarks.trim()) {
            newErrors.remarks = "Remarks are required!";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const loadingToastId = toast.loading("Rejecting... Please wait...");

        const RejectedStatus = {
            approval_status: "rejected",
            remarks: formData.remarks.trim(),
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-stagesupdate-customreject-bankdetails/${i.id}/`,
                RejectedStatus
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Bank Details Request Rejected");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-delete-button"
                title={`Reject Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CrossRed />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Reject Stage {i.level} : {i.role} Approval for{" "}
                                <br />
                                {i?.bank_details?.emp_code} - {i?.bank_details?.emp_name}'s Bank Details Change
                            </h6>

                            <div className="flex-row justify-evenly">
                                <div>
                                    <h6 className="font-weight500 mt-2">Current Details:</h6>
                                    <div className="mb-4">
                                        <table>
                                            <tbody>
                                                {[
                                                    { label: "Account No", value: i?.bank_details?.previous_data?.account_number },
                                                    { label: "IFSC", value: i?.bank_details?.previous_data?.ifsc_code },
                                                    { label: "Bank", value: i?.bank_details?.previous_data?.bank_name },
                                                    { label: "Branch", value: i?.bank_details?.previous_data?.branch_address }
                                                ].map(({ label, value }) => (
                                                    <tr key={label}>
                                                        <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                        <td style={{ padding: "4px 8px" }}>:</td>
                                                        <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Requested Updates:</h6>
                                    <div className="mb-4">
                                        <table>
                                            <tbody>
                                                {[
                                                    { label: "Account No", value: i?.bank_details?.updated_data?.account_number },
                                                    { label: "IFSC", value: i?.bank_details?.updated_data?.ifsc_code },
                                                    { label: "Bank", value: i?.bank_details?.updated_data?.bank_name },
                                                    { label: "Branch", value: i?.bank_details?.updated_data?.branch_address }
                                                ].map(({ label, value }) => (
                                                    <tr key={label}>
                                                        <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                        <td style={{ padding: "4px 8px" }}>:</td>
                                                        <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className='flex-row justify-between  alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Changed Fields:</h6>
                                    <ul>
                                        {i?.bank_details?.changed_fields?.length > 0 ? (
                                            i.bank_details.changed_fields.map((f, idx) => (
                                                <li key={idx}>{f}</li>
                                            ))
                                        ) : (
                                            <li>-</li>
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                                    {i?.bank_details?.proof_attachment ? (
                                        <ViewFile
                                            doc={i?.bank_details?.proof_attachment}
                                            filename={"Proof Attachment"}
                                            filesrc={i?.bank_details?.proof_attachment}
                                        />
                                    ) : (
                                        'No File Attached'
                                    )}
                                </div>

                            </div>
                            <div className='flex-row emp_email_u_body'>
                                <h6 className="font-weight500 mt-2">Reason:</h6>
                                <p className=''>{i?.bank_details?.reason_for_change || '-'}</p>
                            </div>

                            <div className="flex-column mt-3">
                                <label htmlFor="remarks" className="form-labels font-weight500">
                                    Rejection Remarks <span className="required">*</span>
                                </label>
                                <textarea
                                    id="remarks"
                                    name="remarks"
                                    placeholder="Enter reason for rejection"
                                    onChange={(e) =>
                                        setFormData({ ...formData, remarks: e.target.value })
                                    }
                                    value={formData.remarks}
                                    className={`form-input-textarea ${errors.remarks ? "error" : formData.remarks ? "success" : ""
                                        }`}
                                />
                                {errors.remarks && (
                                    <span className="error-message">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-delete font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};



// Investment Declaration Summary Component

// const InvestmentDeclarationSummary = ({ form }) => {
//     if (!form || !form.items) return <div>No declaration data available</div>;

//     const totalDeclared = form.items.reduce((sum, item) => sum + parseFloat(item.declared_amount || 0), 0);
//     const totalVerified = form.items.reduce((sum, item) => sum + parseFloat(item.proof_amount || 0), 0);

//     return (
//         <div className="investment-summary">
//             <table style={{ borderCollapse: 'collapse', width: '100%', border: '0.5px solid gray' }}>
//                 <thead>
//                     <tr>
//                         <th style={{ textAlign: 'left', padding: '4px 8px' }}>Section</th>
//                         <th style={{ textAlign: 'right', padding: '4px 8px' }}>Declared</th>
//                         {/* <th style={{ textAlign: 'right', padding: '4px 8px' }}>Verified</th> */}
//                         <th style={{ textAlign: 'center', padding: '4px 8px' }}>Status</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {form.items.map((item, index) => (
//                         <tr key={item.id}>
//                             <td style={{ padding: '4px 8px' }}>{item.section?.name || `Section ${index + 1}`}</td>
//                             <td style={{ textAlign: 'right', padding: '4px 8px' }}>₹{parseFloat(item.declared_amount || 0).toLocaleString()}</td>
//                             {/* <td style={{ textAlign: 'right', padding: '4px 8px' }}>₹{parseFloat(item.proof_amount || 0).toLocaleString()}</td> */}
//                             <td style={{ textAlign: 'center', padding: '4px 8px' }}>
//                                 <span request-status={item.declaration_status} className='request-status'>
//                                     {/* {item.proof_status || item.declaration_status} */}
//                                     {item.declaration_status}
//                                 </span>
//                             </td>
//                         </tr>
//                     ))}
//                 </tbody>
//                 <tfoot>
//                     <tr style={{ borderTop: '1px solid #ddd' }}>
//                         <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Total</td>
//                         <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 'bold' }}>₹{totalDeclared.toLocaleString()}</td>
//                         {/* <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 'bold' }}>₹{totalVerified.toLocaleString()}</td> */}
//                         <td></td>
//                     </tr>
//                 </tfoot>
//             </table>
//         </div>
//     );
// };

const InvestmentDeclarationSummary = ({ form }) => {
    if (!form || !form.items) return <div>No declaration data available</div>;

    const totalDeclared = form.items.reduce((sum, item) => sum + parseFloat(item.final_amount || 0), 0);
    const totalVerified = form.items.reduce((sum, item) => sum + parseFloat(item.proof_amount || 0), 0);

    return (
        <div className="inv-summary-card">
            {/* <h4 className="inv-title">Declaration Details:</h4> */}

            <table className="inv-table">
                <thead>
                    <tr>
                        <th className='align-leftt font-size-text'>Section</th>
                        <th className="align-right font-size-text" >Final Amount</th>
                        {/* <th className="dy-align-right">Verified</th> */}
                        <th className="align-center font-size-text" style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                </thead>

                <tbody>
                    {form.items.map((item, index) => (
                        <tr key={item.id} >
                            <td className="align-leftt py-2 font-size-text table-item-cell"

                            >{item.items_details?.section_name || `Section ${index + 1}`}</td>

                            <td className="py-2 font-size-text align-right"
                                style={{
                                    width: '120px',
                                    cursor: 'help'
                                }}
                            >
                                {/* ₹{parseFloat(item.final_amount || 0).toLocaleString()} */}
                                <CustomTooltip
                                    position="top"
                                    tooltipContent={
                                        <div>
                                            <div>Declared Amount: <strong>{formatCurrencyIndian(item?.declared_amount)}</strong></div>
                                            <div>Final Amount: <strong>{formatCurrencyIndian(item?.final_amount)}</strong></div>
                                        </div>
                                    }
                                >
                                    <span style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',

                                    }}>
                                        {formatCurrencyIndian(item?.final_amount)}
                                    </span>
                                </CustomTooltip>
                            </td>

                            <td className="align-center py-2 font-size-text">
                                <span request-status={item.declaration_status} className='request-status text-capitalize'>
                                    {item.declaration_status}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>

                <tfoot>
                    <tr>
                        <td className="dy-bold text-left font-12">Total</td>
                        <td className="dy-align-right dy-bold font-12">₹{totalDeclared.toLocaleString()}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};


// Investment Proof Summary Component

const InvestmentProofSummaryBackup = ({ form }) => {
    if (!form || !form.items) return <div>No proof data available</div>;

    const itemsWithProofs = form.items.filter(item =>
        item.proofs && item.proofs.length > 0
    );

    return (
        <div className="proof-summary">
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Section</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px' }}>Declared</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px' }}>Proof Amount</th>
                        <th style={{ textAlign: 'center', padding: '4px 8px' }}>Documents</th>
                        <th style={{ textAlign: 'center', padding: '4px 8px' }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {itemsWithProofs.map((item, index) => (
                        <tr key={item.id}>
                            <td style={{ padding: '4px 8px' }}>{item.section?.name || `Section ${index + 1}`}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px' }}>₹{parseFloat(item.declared_amount || 0).toLocaleString()}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px' }}>₹{parseFloat(item.proof_amount || 0).toLocaleString()}</td>
                            <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                {item.proofs?.map((proof, idx) => (
                                    <ViewFile
                                        key={proof.id}
                                        doc={proof.document || proof.document_url}
                                        filename={`Proof ${idx + 1}`}
                                        filesrc={proof.document || proof.document_url}
                                    />
                                ))}
                            </td>
                            <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                <span className={`status-badge ${item.proof_status}`}>
                                    {item.proof_status}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {itemsWithProofs.length === 0 && (
                <p style={{ textAlign: 'center', color: '#666' }}>No proofs submitted yet</p>
            )}
        </div>
    );
};

// Enhanced InvestmentProofSummary Component
const InvestmentProofSummary = ({ form }) => {

    const [selectedItem, setSelectedItem] = useState(null);

    const [viewProof, setViewProof] = useState(false);
    const [selectedProofs, setSelectedProofs] = useState([]);
    if (!form || !form.items) return <div>No proof data available</div>;

    const itemsWithProofs = form.items.filter(item =>
        // item.proofs && item.proofs.length > 0
        item.proof_amount >= 0
    )

    const totalDeclared = itemsWithProofs.reduce((sum, item) => sum + parseFloat(item.declared_amount || 0), 0);
    const totalProofAmount = itemsWithProofs.reduce((sum, item) => sum + parseFloat(item.proof_amount || 0), 0);
    // const totalVerified = itemsWithProofs.reduce((sum, item) => {
    //     const verifiedProofs = item.proofs.filter(proof => proof.status === 'verified');
    //     const verifiedAmount = verifiedProofs.reduce((proofSum, proof) => proofSum + parseFloat(proof.proof_amt || 0), 0);
    //     return sum + verifiedAmount;
    // }, 0);

    const openProofModal = (proofs = []) => {
        setSelectedProofs(proofs || []);
        setViewProof(true);
    };


    const getProofStatus = (proofs = []) => {
        if (proofs.some(p => p.status === 'pending')) return 'pending';
        if (proofs.every(p => p.status === 'verified')) return 'verified';
        if (proofs.some(p => p.status === 'approved')) return 'approved';
        return 'submitted';
    };



    return (
        <div className="inv-summary-card" style={{ overflowX: 'auto' }}>
            <table className="inv-table">
                <thead>
                    <tr>
                        <th className='align-leftt font-size-text'>Section</th>
                        <th className='font-size-text dy-align-center'>Declared Amount</th>
                        <th className='font-size-text dy-align-center'>Total Proof Amount</th>
                        <th className='font-size-text dy-align-center'>View Proofs</th>
                        <th className='align-leftt font-size-text' style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {itemsWithProofs.map((item, index) => {
                        const verifiedProofs = item.proofs.filter(proof => proof.status === 'verified');
                        const verifiedAmount = verifiedProofs.reduce((sum, proof) => sum + parseFloat(proof.proof_amt || 0), 0);
                        const proofStatus = getProofStatus(item.proofs);

                        let calculationProofObject = {
                            calculation_amount: item.calculation_amount,
                            calculation_remarks: item.calculation_remarks,
                            calculation_attachment: item.calculation_attachment,
                        }

                        return (
                            <tr key={item.id}>
                                <td className='align-leftt py-2 font-size-text table-item-cell'>
                                    {item?.items_details
                                        ?.section_name || `Section ${index + 1}`}
                                    <br />
                                    <small style={{ color: '#666' }}>{item?.items_details?.section_code || ''}</small>
                                </td>
                                {/* <td className="py-2 font-size-text align-right" style={{ width: '120px' }}>
                                    ₹{parseFloat(item.declared_amount || 0).toLocaleString()}
                                </td> */}
                                <td className="py-2 font-size-text dy-align-center help-sign"

                                >

                                    <CustomTooltip
                                        position="top"
                                        tooltipContent={
                                            <div>
                                                <div>Declared Amount: <strong>{formatCurrencyIndian(Number(item.declared_amount) || 0)}</strong></div>
                                                <div>Final Amount: <strong>{formatCurrencyIndian(Number(item?.final_amount) || 0)}</strong></div>
                                            </div>
                                        }
                                    >
                                        <span style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',

                                        }}>
                                            {formatCurrencyIndian(Number(item?.declared_amount) || 0)}
                                        </span>
                                    </CustomTooltip>
                                </td>
                                <td className="py-2 font-size-text dy-align-center help-sign"

                                >

                                    <CustomTooltip
                                        position="top"
                                        tooltipContent={
                                            <div>
                                                <div>Proof Amount: <strong>{formatCurrencyIndian(Number(item.proof_amount) || 0)}</strong></div>
                                                <div>Calculation Amount: <strong>{formatCurrencyIndian(Number(item?.calculation_amount) || 0)}</strong></div>
                                            </div>
                                        }
                                    >
                                        <span style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',

                                        }}>
                                            {formatCurrencyIndian(Number(item?.proof_amount) || 0)}
                                        </span>
                                    </CustomTooltip>
                                </td>



                                <td className='dy-align-center'>
                                    <button
                                        className="text-button allproject-dashboard"
                                        // onClick={() => openProofModal(item.proofs)}
                                        onClick={() => {
                                            setSelectedItem(item);
                                            setViewProof(true);
                                        }}

                                    >
                                        <BlueEye />
                                    </button>


                                </td>

                                <td style={{ textAlign: 'center', padding: '4px 8px' }}>

                                    {/* <span className={`status-badge ${proofStatus}`}>
                                        {proofStatus}
                                    </span> */}
                                    <span className={`request-status text-capitalize`} request-status={item.proof_status}>
                                        {item.proof_status || 'Submitted'}
                                    </span>


                                </td>
                            </tr>
                        );
                    })}
                    <ProofViewModal show={viewProof} onClose={() => setViewProof(false)}
                        // proofs={selectedProofs}
                        //    calculationProofsData={calculationProofObject} 
                        proofs={selectedItem?.proofs || []}
                        calculationProofsData={{
                            calculation_amount: selectedItem?.calculation_amount,
                            calculation_remarks: selectedItem?.calculation_remarks,
                            calculation_attachment: selectedItem?.calculation_attachment,
                        }}
                    />
                </tbody>
                <tfoot>
                    <tr style={{ borderTop: '1px solid #ddd', fontWeight: 'bold' }}>
                        <td className='align-leftt' style={{ padding: '4px 8px' }}>Total</td>
                        <td className='dy-align-center' style={{ padding: '4px 8px' }}>₹{totalDeclared.toLocaleString()}</td>
                        <td className='dy-align-center' style={{ padding: '4px 8px' }}>₹{totalProofAmount.toLocaleString()}</td>
                        {/* <td style={{ textAlign: 'right', padding: '4px 8px' }}>₹{totalVerified.toLocaleString()}</td> */}
                        <td></td>
                        <td></td>

                    </tr>
                </tfoot>
            </table>
            {itemsWithProofs.length === 0 && (
                <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No investment proofs submitted yet</p>
            )}
        </div>
    );
};



// Investment Declaration and Proof Approvals

//DynamicApprovalInvestmentDeclarationAction

const DynamicApprovalInvestmentDeclarationAction = ({
    context,
    i,
    approvalType,
    getDynamicRequestData,
    getApprovalStages
}) => {

    const permission = i?.permissions;

    const PERMISSIONS = {
        APPROVE_REJECT: "Approve-Reject", // Approve, Reject
        APPROVE_REJECT_EDIT: "Approve-Reject-Edit", //Approve, Edit-Approve, Reject
        FINALIZE: "Finalize", // Finalize, Reject
        FINALIZE_EDIT: "Finalize-Edit", //Finalize, Edit-Finalize, Reject
    };

    const actionConfig = {
        [PERMISSIONS.APPROVE_REJECT]: ["approve", "reject"],
        [PERMISSIONS.APPROVE_REJECT_EDIT]: ["approve", "edit", "reject"],
        [PERMISSIONS.FINALIZE]: ["finalize", "reject"],
        [PERMISSIONS.FINALIZE_EDIT]: ["finalize", "edit", "reject"],
    };

    const allowedActions = actionConfig[permission] || [];

    const [action, setAction] = useState(null);
    const [show, setShow] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [errors, setErrors] = useState({});
    const [editAmounts, setEditAmounts] = useState({});
    const [reasons, setReasons] = useState({});

    const isReject = action === "reject";
    const isEditApproval = action === "edit";
    const isFinalize = action === "finalize";
    const isApprove = action === "approve";

    const requiresRemarks = isReject || isEditApproval;


    const handleShow = () => {
        if (isEditApproval) {
            const amountObj = {};
            const reasonObj = {};
            // const obj = {};
            i.investment_form.items.forEach(item => {
                // obj[item.id] = parseFloat(item.final_amount) || 0;
                amountObj[item.id] = parseFloat(item.final_amount) || 0;
                reasonObj[item.id] = item.amount_change_reason || "";
            });
            // setEditAmounts(obj);
            setEditAmounts(amountObj);
            setReasons(reasonObj);
        }
        setRemarks("");
        setErrors({});
        setShow(true);
    };

    const handleClose = () => setShow(false);

    const validateForm = () => {
        const err = {};
        const isApprovalFlow = isEditApproval || isReject;

        if (isApprovalFlow && !remarks.trim()) {
            err.remarks = "Remarks are required!";
        }

        if (isApprovalFlow && i?.investment_form?.items?.length > 0) {
            const amountErrors = {};
            const reasonErrors = {};

            // i.investment_form.items.forEach(item => {
            //     const editedAmount =
            //         editAmounts[item.id] === undefined
            //             ? item.final_amount
            //             : editAmounts[item.id];

            //     if (!editedAmount || Number(editedAmount) <= 0) {
            //         amountErrors[item.id] = "Amount is required!";
            //     }
            //     if (Number(editedAmount) <= 0) {
            //         amountErrors[item.id] = "Amount must be greater than 0!";
            //     }

            //     if (!reasons[item.id]?.trim()) {
            //         reasonErrors[item.id] = "Reason is required!";
            //     }
            // });
            i.investment_form.items.forEach(item => {
                const editedAmount = editAmounts[item.id];
                const originalAmount = Number(item.final_amount || 0);

                const hasAmountChanged =
                    editedAmount !== undefined &&
                    editedAmount !== "" &&
                    Number(editedAmount) !== originalAmount;

                const isRowTouched = hasAmountChanged;


                if (isRowTouched) {

                    if (editedAmount === "" || editedAmount === undefined) {
                        amountErrors[item.id] = "Amount is required!";
                    } else if (Number(editedAmount) <= 0) {
                        amountErrors[item.id] = "Amount must be greater than 0!";
                    }

                    if (!reasons[item.id]?.trim()) {
                        reasonErrors[item.id] = "Reason is required!";
                    }
                }
            });


            if (Object.keys(amountErrors).length) err.amount = amountErrors;
            if (Object.keys(reasonErrors).length) err.reason = reasonErrors;
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };


    const API_URL = `${BASE_URL}/wfm/dynamicapproval-investment-all-approval/${i.id}/`


    const handleSubmit = async () => {
        if (!validateForm()) return;

        const loadingToast = toast.loading("Processing...");

        let payload = {
            approval_status: isReject ? "rejected" : "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code")
        };


        if (isReject) {
            payload.remarks = remarks.trim();
        }

        if (isEditApproval) {
            const updatedItems = i.investment_form.items
                .filter(item =>
                    editAmounts[item.id] !== undefined ||
                    reasons[item.id]?.trim()
                )
                .map(item => ({
                    id: item.id,
                    final_amount: editAmounts[item.id] ?? item.final_amount,
                    amount_change_reason: reasons[item.id] || ""
                }));


            payload.remarks = remarks.trim();
            // payload.form_update_data = {
            //     items: updatedItems
            // };

            payload.form_items_update_data = updatedItems
        }



        try {
            const res = await axios.put(API_URL, payload);

            if (res.status === 200 || res.status === 201) {
                toast.success(
                    isReject
                        ? "Request Rejected"
                        : isFinalize
                            ? "Request Finalized"
                            : isEditApproval
                                ? "Approved with Adjustments"
                                : "Request Approved"
                );
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${res.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToast);
        } finally {
            toast.dismiss(loadingToast);
            handleClose();
        }
    };

    const summaryComponentMap = {
        investment_declaration: InvestmentDeclarationSummary,

    };


    const SummaryComponent =
        summaryComponentMap[approvalType] || null;



    const actionButtonConfig = {
        approve: {
            title: "Approve",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        edit: {
            title: "Edit & Approve",
            className: "model-partial-button",
            icon: <WarnYellow />,
        },
        finalize: {
            title: "Finalize",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        reject: {
            title: "Reject",
            className: "model-delete-button",
            icon: <CrossRed />,
        },
    };

    return (
        <>
            {/* <button
                className={buttonConfig[type].className}
                title={buttonConfig[type].title}
                onClick={handleShow}
            >
                {buttonConfig[type].icon}
            </button> */}
            {allowedActions.map((actionType) => {
                const config = actionButtonConfig[actionType];
                if (!config) return null;

                return (
                    <button
                        key={actionType}
                        className={config.className}
                        title={config.title}
                        onClick={() => {
                            setAction(actionType);
                            handleShow();
                        }}
                    >
                        {config.icon}
                    </button>
                );
            })}

            <Modal show={show} onHide={handleClose}
                dialogClassName={action === "edit" ? "three-fourth-modal" : "custom-modal"}
            >
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">

                            {/* Heading section */}
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                {isApprove && (
                                    <>
                                        {`Confirm Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}
                                {isEditApproval && (
                                    <>
                                        {`Edit & Approve Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>

                                )}
                                {isReject && (
                                    <>
                                        {`Reject Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}
                                {isFinalize && (
                                    <>
                                        {`Finalize Stage ${i.level} : ${i.role}`}  Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}

                            </h6>

                            {/* Tax Regime detail */}
                            <div className='flex-column justify-between'>
                                <div className='flex flex-row gap-2'>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <div>{i?.investment_form?.regime_details?.name || 'Not specified'}</div>
                                    ({formatDate(i?.investment_form?.regime_details?.financial_year_start, true).MonthYear}
                                    {" "}-{" "}
                                    {formatDate(i?.investment_form?.regime_details?.financial_year_end, true).MonthYear})
                                </div>
                                <div className="rounded flex-column mt-2"
                                    style={{
                                        border: "1px solid #e0e0e0",
                                        padding: "16px",
                                        backgroundColor: "#fafafa",
                                        marginBottom: '20px',
                                        alignItems: "flex-start"
                                    }}>
                                    <div className='form-flex-wrap w-full mb-2'>
                                        {i?.investment_form?.prev_company_salary && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Previous Company Salary:</h6>
                                                <div>
                                                    {i?.investment_form?.prev_company_salary}
                                                </div>
                                            </div>
                                        )}
                                        {i?.investment_form?.prev_company_salary_doc && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Salary Document:</h6>
                                                <div>

                                                    <a href={i?.investment_form?.prev_company_salary_doc} target="_blank" rel="noreferrer">
                                                        <AttachmentIcon source={i?.investment_form?.prev_company_salary_doc} />
                                                    </a>

                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className='form-flex-wrap w-full'>
                                        {i?.investment_form?.prev_company_tax && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Previous Company Tax:</h6>
                                                <div>
                                                    {i?.investment_form?.prev_company_tax}
                                                </div>
                                            </div>
                                        )}

                                        {i?.investment_form?.prev_company_tax_doc && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Tax Document:</h6>
                                                <div>
                                                    <a href={i?.investment_form?.prev_company_tax_doc} target="_blank" rel="noreferrer">
                                                        <AttachmentIcon source={i?.investment_form?.prev_company_tax_doc} />
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Declaration Summary */}

                            <div className="flex-column justify-evenly mt-2 align-center" style={{ paddingLeft: '0px' }}>

                                <h6 className="font-weight500  mb-3">
                                    {isReject
                                        ? "Declaration Summary:"
                                        : isFinalize
                                            ?
                                            "Declaration Summary:"
                                            : isEditApproval
                                                ? "Adjust Declared Amounts:"
                                                : "Declaration Details:"}

                                </h6>
                                {!isEditApproval && <SummaryComponent form={i.investment_form} />}
                            </div>


                            {/* Declaration summary table  for Edit */}
                            {isEditApproval && (
                                <div >
                                    {/* <h6 className="font-weight500"a>Adjust Declared Amounts:</h6> */}
                                    <div
                                        //  style={{ width: "100%", overflowX: "auto" }}
                                        className='table-css-white-background scroll-container-table' style={{ minHeight: '0px' }}>
                                        <table className="inv-table table-css">
                                            <thead>
                                                <tr>
                                                    <th className="dy-align-left">Section</th>
                                                    <th className="dy-align-right">Current Amount</th>
                                                    <th className="dy-align-right">Final Amount</th>
                                                    <th className="dy-align-center">Reason</th>
                                                    <th className="dy-align-center">Status</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {i.investment_form.items.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td className="dy-align-left table-item-cell"

                                                        >
                                                            <strong style={{
                                                                overflow: 'hidden',

                                                            }}

                                                                title={item.items_details?.section_name || `Section ${index + 1}`}
                                                            >{item.items_details?.section_name || `Section ${index + 1}`}</strong>
                                                            <br />
                                                            <small style={{ color: '#666' }}>{item.section?.code || ''}</small>
                                                        </td>

                                                        <td className="dy-align-center"
                                                            a
                                                        >
                                                            ₹{parseFloat(item.declared_amount || 0).toLocaleString()}
                                                        </td>

                                                        <td className="dy-align-right">
                                                            <input
                                                                type="number"
                                                                // value={editAmounts[item.id] || item.final_amount}
                                                                value={editAmounts[item.id] === undefined ? item.final_amount : editAmounts[item.id]}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setEditAmounts({
                                                                        ...editAmounts,
                                                                        [item.id]: val === "" ? "" : Number(val)
                                                                    });
                                                                    if (errors.amount?.[item.id]) {
                                                                        setErrors(prev => ({
                                                                            ...prev,
                                                                            amount: { ...prev.amount, [item.id]: "" }
                                                                        }));
                                                                    }
                                                                }}

                                                                step={0.01}
                                                                onWheel={(e) => e.target.blur()}
                                                                className={`form-input-tabular text-start ${errors.amount?.[item.id] ? "error" : ""}`}
                                                                style={{ width: "120px", borderBottom: '0.5px solid gray', margin: '5px' }}
                                                                min={0}
                                                                placeholder='Amount'
                                                            />
                                                            {errors.amount?.[item.id] && (
                                                                <div className="error-message font-size-text mt-1">{errors.amount[item.id]}</div>
                                                            )}
                                                        </td>
                                                        <td className='dy-align-center'>
                                                            <input
                                                                type="text"
                                                                value={reasons[item.id] || ""}
                                                                onChange={(e) => {
                                                                    setReasons({
                                                                        ...reasons,
                                                                        [item.id]: e.target.value
                                                                    })
                                                                    if (errors.reason?.[item.id]) {
                                                                        setErrors(prev => ({
                                                                            ...prev,
                                                                            reason: { ...prev.reason, [item.id]: "" }
                                                                        }));
                                                                    }
                                                                }
                                                                }

                                                                className={`form-input-tabular text-start ${errors.reason?.[item.id] ? "error" : ""}`}
                                                                placeholder='Change reason'
                                                                style={{ width: "120px", borderBottom: '0.5px solid gray', margin: '5px' }}

                                                            />
                                                            {errors.reason?.[item.id] && (
                                                                <div className="error-message font-size-text mt-1">{errors.reason[item.id]}</div>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>
                                                            <span className={`request-status text-capitalize`} request-status={item.declaration_status}>
                                                                {item.declaration_status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}


                            {(isEditApproval || isReject) && (
                                <div className="mt-3 flex flex-column">
                                    <label className="form-labels font-weight500">
                                        {isReject ? "Rejection Remarks" : "Approval Remarks"}  <span className="required">*</span>
                                    </label>
                                    <textarea
                                        rows="3"
                                        name='remarks'
                                        placeholder={isReject ? "Enter reason for rejection" : "Enter remarks for amount adjustments"}
                                        className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                        value={remarks}
                                        style={{ width: 'auto' }}
                                        onChange={(e) => {
                                            setRemarks(e.target.value)
                                            if (errors.remarks) {
                                                setErrors((prev) => ({ ...prev, remarks: "" }))
                                            }
                                        }
                                        }
                                    ></textarea>
                                    {errors.remarks && (
                                        <div className="error-message">{errors.remarks}</div>
                                    )}
                                </div>
                            )}

                            {/* Display Note */}

                            {isApprove && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This approval will set all section status to <strong>approved</strong>.
                                </div>
                            )}
                            {isEditApproval && (
                                <div className='margin_top padding_ color-tab-yellow text-warning-color-dark notice-badge'>
                                    This approval will update the declared amounts and set all section status to <strong>approved</strong>.
                                </div>
                            )}
                            {isReject && (
                                <div className='margin_top padding_ color-badge-danger color-border-red notice-badge'>
                                    This rejection will set all section status to <strong>rejected</strong>. The form will need to be resubmitted, after which the approval process will restart.
                                </div>
                            )}
                            {isFinalize && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This action will finalize all sections and lock the data from further changes.
                                </div>
                            )}


                            {/* Buttons */}
                            <div className="button-models mt-4">
                                <button className="model-button model-button-cancel" onClick={handleClose}>
                                    Cancel
                                </button>

                                <button className={`model-button ${isReject ? 'model-button-delete font-weight500 font-size-heading' : 'model-button-submit'} `} onClick={handleSubmit}>
                                    {isReject
                                        ? "Reject"
                                        : isEditApproval
                                            ? "Approve with Adjustments"
                                            : isFinalize
                                                ? "Finalize"
                                                : "Approve"}
                                </button>
                            </div>

                        </div>
                    </div >
                </Modal.Body >
            </Modal >
        </>
    );
};

//DynamicApprovalInvestmentProofsAction
const DynamicApprovalInvestmentProofsAction = ({
    context,
    i,
    approvalType,
    getDynamicRequestData,
    getApprovalStages
}) => {


    const permission = i?.permissions;

    const PERMISSIONS = {
        APPROVE_REJECT: "Approve-Reject", // Approve, Reject
        APPROVE_REJECT_EDIT: "Approve-Reject-Edit", //Approve, Edit-Approve, Reject
        FINALIZE: "Finalize", // Finalize, Reject
        FINALIZE_EDIT: "Finalize-Edit", //Finalize, Edit-Finalize, Reject
    };

    const actionConfig = {
        [PERMISSIONS.APPROVE_REJECT]: ["approve", "reject"],
        [PERMISSIONS.APPROVE_REJECT_EDIT]: ["approve", "edit", "reject"],
        [PERMISSIONS.FINALIZE]: ["finalize", "reject"],
        [PERMISSIONS.FINALIZE_EDIT]: ["finalize", "edit", "reject"],
    };

    const allowedActions = actionConfig[permission] || [];

    const [action, setAction] = useState(null);
    const [show, setShow] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [errors, setErrors] = useState({});
    const [editAmounts, setEditAmounts] = useState({});
    const [reasons, setReasons] = useState({});
    const [viewProof, setViewProof] = useState(false);
    const [selectedProofs, setSelectedProofs] = useState([]);
    const [updatedProofs, setUpdatedProofs] = useState({});

    const isReject = action === "reject";
    const isEditApproval = action === "edit";
    const isFinalize = action === "finalize";
    const isApprove = action === "approve";

    const requiresRemarks = isReject || isEditApproval;

    const handleShow = () => {
        if (isEditApproval) {
            const amountObj = {};
            const reasonObj = {};
            // const obj = {};
            i.investment_form.items.forEach(item => {
                // obj[item.id] = parseFloat(item.final_amount) || 0;
                amountObj[item.id] = parseFloat(item.final_amount) || 0;
                reasonObj[item.id] = item.amount_change_reason || "";
            });
            // setEditAmounts(obj);
            setEditAmounts(amountObj);
            setReasons(reasonObj);
        }
        setRemarks("");
        setErrors({});
        setShow(true);
    };

    const handleClose = () => setShow(false);

    const validateForm = () => {
        const err = {};


        if (requiresRemarks && !remarks.trim()) {
            err.remarks = "Remarks are required!";
        }


        if (isEditApproval && i?.investment_form?.items?.length > 0) {
            const amountErrors = {};
            const reasonErrors = {};
            const proofErrors = {};

            // i.investment_form.items.forEach(item => {
            //     const editedAmount = editAmounts[item.id];


            //     if (editedAmount === "" || editedAmount === undefined || Number(editedAmount) <= 0) {
            //         amountErrors[item.id] = "Amount is required";
            //     }
            //     if (Number(editedAmount) <= 0) {
            //         amountErrors[item.id] = "Amount must be greater than 0!";
            //     }

            //     if (!reasons[item.id]?.trim()) {
            //         reasonErrors[item.id] = "Reason is required";
            //     }


            //     if (hasManualItems) {
            //         if (!updatedProofs?.[item.id] || updatedProofs[item.id].length === 0) {
            //             proofErrors[item.id] = "Document is required";
            //         }
            //     }
            // });

            i.investment_form.items.forEach(item => {
                const editedAmount = editAmounts[item.id];
                const originalAmount = Number(item.calculation_amount || 0);

                const hasAmountChanged =
                    editedAmount !== undefined &&
                    editedAmount !== "" &&
                    Number(editedAmount) !== originalAmount;

                const hasProofUpdated =
                    hasManualItems && updatedProofs?.[item.id]?.length > 0;

                const isRowTouched = hasAmountChanged || hasProofUpdated;


                if (isRowTouched) {


                    if (editedAmount === "" || editedAmount === undefined) {
                        amountErrors[item.id] = "Amount is required";
                    } else if (Number(editedAmount) <= 0) {
                        amountErrors[item.id] = "Amount must be greater than 0!";
                    }

                    // Reason validation
                    if (!reasons[item.id]?.trim()) {
                        reasonErrors[item.id] = "Reason is required";
                    }


                    if (hasManualItems) {
                        if (!updatedProofs?.[item.id] || updatedProofs[item.id].length === 0) {
                            proofErrors[item.id] = "Document is required";
                        }
                    }
                }
            });

            if (Object.keys(amountErrors).length > 0) {
                err.amount = amountErrors;
            }

            if (Object.keys(reasonErrors).length > 0) {
                err.reason = reasonErrors;
            }

            if (hasManualItems && Object.keys(proofErrors).length > 0) {
                err.updatedProofs = proofErrors;
            }
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };


    const API_URL = `${BASE_URL}/wfm/dynamicapproval-investment-all-approval/${i.id}/`


    const handleSubmit = async () => {

        if (!validateForm()) return;

        const loadingToast = toast.loading("Processing...");

        let payload = {
            approval_status: isReject ? "rejected" : "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        if (isReject) {
            payload.remarks = remarks.trim();
        }

        if (isEditApproval) {
            const updatedItems = i.investment_form.items
                .filter(item =>
                    editAmounts[item.id] !== undefined ||
                    reasons[item.id]?.trim() ||
                    (updatedProofs[item.id] && updatedProofs[item.id].length > 0)
                )
                .map(item => ({
                    id: item.id,
                    calculation_amount: editAmounts[item.id] ?? null,
                    calculation_remarks: reasons[item.id] ?? "",
                    calculation_attachment: updatedProofs[item.id] || []
                }));

            payload.remarks = remarks.trim();
            payload.form_items_update_data = updatedItems;
        }


        // For proof, use FormData for file uploads
        if (i.format_for === "investment_proof" && isEditApproval) {
            const formData = new FormData();

            // Add basic fields
            formData.append("approval_status", payload.approval_status);
            formData.append("updated_at", payload.updated_at);
            formData.append("updated_by", payload.updated_by);
            if (payload.remarks) {
                formData.append("remarks", payload.remarks);
            }

            // Add form_items_update_data
            if (payload.form_items_update_data && payload.form_items_update_data.length > 0) {
                payload.form_items_update_data.forEach((item, index) => {
                    formData.append(`form_items_update_data[${index}][id]`, item.id);

                    if (item.calculation_amount !== null) {
                        formData.append(
                            `form_items_update_data[${index}][calculation_amount]`,
                            item.calculation_amount
                        );
                    }

                    if (item.calculation_remarks) {
                        formData.append(
                            `form_items_update_data[${index}][calculation_remarks]`,
                            item.calculation_remarks
                        );
                    }

                    // Handle file attachments
                    item.calculation_attachment?.forEach((file, fIndex) => {
                        const customFile = generateCustomFileForUpload(file, "Aimantra");
                        formData.append(
                            `form_items_update_data[${index}][calculation_attachment][${fIndex}]`,
                            customFile
                        );
                    });
                });
            }




            try {
                const res = await axios.put(API_URL, formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });

                if (res.status === 200 || res.status === 201) {
                    toast.success(
                        isReject
                            ? "Request Rejected"
                            : isFinalize
                                ? "Request Finalized"
                                : isEditApproval
                                    ? "Approved with Adjustments"
                                    : "Request Approved"
                    );
                    await getDynamicRequestData();
                    await getApprovalStages();
                } else {
                    toast.error(`Error: ${res.statusText}`);
                }
            } catch (err) {
                handleErrorToast(err, loadingToast);
            } finally {
                toast.dismiss(loadingToast);
                handleClose();
            }
        }
        else {
            try {
                const res = await axios.put(API_URL, payload);

                if (res.status === 200 || res.status === 201) {
                    toast.success(
                        isReject
                            ? "Request Rejected"
                            : isFinalize
                                ? "Request Finalized"
                                : isEditApproval
                                    ? "Approved with Adjustments"
                                    : "Request Approved"
                    );
                    await getDynamicRequestData();
                    await getApprovalStages();
                } else {
                    toast.error(`Error: ${res.statusText}`);
                }
            } catch (err) {
                handleErrorToast(err, loadingToast);
            } finally {
                toast.dismiss(loadingToast);
                handleClose();
            }
        }
    };

    const summaryComponentMap = {

        investment_proof: InvestmentProofSummary,

    };

    const SummaryComponent =
        summaryComponentMap[approvalType] || '';


    const actionButtonConfig = {
        approve: {
            title: "Approve",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        edit: {
            title: "Edit & Approve",
            className: "model-partial-button",
            icon: <WarnYellow />,
        },
        finalize: {
            title: "Finalize",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        reject: {
            title: "Reject",
            className: "model-delete-button",
            icon: <CrossRed />,
        },
    };

    const openProofModal = (proofs = []) => {
        setSelectedProofs(proofs || []);
        setViewProof(true);
    };

    const hasManualItems = i?.investment_form?.items?.some(
        item => item?.items_details?.section_is_manual
    );

    const getProofStatus = (proofs = []) => {
        if (proofs.some(p => p.status === 'pending')) return 'pending';
        if (proofs.every(p => p.status === 'verified')) return 'verified';
        if (proofs.some(p => p.status === 'approved')) return 'approved';
        return 'submitted';
    };

    useEffect(() => {
        if (i?.investment_form?.items?.length) {
            const initialAmounts = {};
            i.investment_form.items.forEach(item => {
                initialAmounts[item.id] =
                    item.calculation_amount !== null
                        ? Number(item.calculation_amount)
                        : "";
            });
            setEditAmounts(initialAmounts);
        }
    }, [i?.investment_form?.items]);


    return (
        <>
            {allowedActions.map((actionType) => {
                const config = actionButtonConfig[actionType];
                if (!config) return null;

                return (
                    <button
                        key={actionType}
                        className={config.className}
                        title={config.title}
                        onClick={() => {
                            setAction(actionType);
                            handleShow();
                        }}
                    >
                        {config.icon}
                    </button>
                );
            })}

            <Modal show={show} onHide={handleClose}

                dialogClassName={action === "edit" ? "three-fourth-modal" : "custom-modal"}
            >
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">

                            {/* Heading section */}
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                {isApprove && (
                                    <>
                                        {`Confirm Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}
                                {isEditApproval && (
                                    <>
                                        {`Edit & Approve Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>

                                )}
                                {isReject && (
                                    <>
                                        {`Reject Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}
                                {isFinalize && (
                                    <>
                                        {`Finalize Stage ${i.level} : ${i.role}`}  Approval for
                                        <br />
                                        {`${i.investment_form.emp_code}-${i.investment_form.emp_name}'s ${context}`}
                                    </>
                                )}

                            </h6>

                            {/* Tax Regime detail */}
                            <div className='flex-column justify-between'>
                                <div className='flex flex-row gap-2'>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <div>{i?.investment_form?.regime_details?.name || 'Not specified'}</div>
                                    ({formatDate(i?.investment_form?.regime_details?.financial_year_start, true).MonthYear}
                                    {" "}-{" "}
                                    {formatDate(i?.investment_form?.regime_details?.financial_year_end, true).MonthYear})
                                </div>

                                <div className="rounded flex-column mt-2"
                                    style={{
                                        border: "1px solid #e0e0e0",
                                        padding: "16px",
                                        backgroundColor: "#fafafa",
                                        marginBottom: '20px',
                                        alignItems: "flex-start"
                                    }}>
                                    <div className='form-flex-wrap w-full mb-2'>
                                        {i?.investment_form?.prev_company_salary && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Previous Company Salary:</h6>
                                                <div>
                                                    {i?.investment_form?.prev_company_salary}
                                                </div>
                                            </div>
                                        )}
                                        {i?.investment_form?.prev_company_salary_doc && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Salary Document:</h6>
                                                <div>

                                                    <a href={i?.investment_form?.prev_company_salary_doc} target="_blank" rel="noreferrer">
                                                        <AttachmentIcon source={i?.investment_form?.prev_company_salary_doc} />
                                                    </a>

                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className='form-flex-wrap w-full'>
                                        {i?.investment_form?.prev_company_tax && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Previous Company Tax:</h6>
                                                <div>
                                                    {i?.investment_form?.prev_company_tax}
                                                </div>
                                            </div>
                                        )}

                                        {i?.investment_form?.prev_company_tax_doc && (
                                            <div className='flex flex-row gap-2'>
                                                <h6 className="font-weight500 mt-2">Tax Document:</h6>
                                                <div>
                                                    <a href={i?.investment_form?.prev_company_tax_doc} target="_blank" rel="noreferrer">
                                                        <AttachmentIcon source={i?.investment_form?.prev_company_tax_doc} />
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Proof Summary */}

                            <div className="flex-column justify-evenly mt-2 align-center" style={{ paddingLeft: '0px' }}>

                                <h6 className="font-weight500  mb-3">
                                    {isReject
                                        ? "Proof Summary:"
                                        : isFinalize
                                            ?
                                            "Proof Summary:"
                                            : isEditApproval
                                                ? "Adjust Proof Amounts:"
                                                : "Proof Details:"}

                                </h6>
                                {!isEditApproval && <SummaryComponent form={i.investment_form} />}
                            </div>


                            {/* Proof summary table  for Edit */}
                            {isEditApproval && (
                                <div >
                                    {/* <h6 className="font-weight500">Adjust Declared Amounts:</h6> */}
                                    <div
                                        className='table-css-white-background scroll-container-table' style={{ minHeight: '0px' }}
                                    >
                                        <table className="inv-table table-css">
                                            <thead>
                                                <tr>
                                                    <th className='align-leftt font-size-text'>Section</th>
                                                    <th className="dy-align-right">Declared Amount</th>
                                                    <th className="dy-align-right">Total Proof Amount</th>
                                                    <th className="dy-align-center">Calculation Amount</th>
                                                    <th className='align-leftt font-size-text'>View Proofs</th>
                                                    {hasManualItems && (
                                                        <th className='align-leftt font-size-text'>Update Proofs</th>
                                                    )}
                                                    <th className="dy-align-center">Reason</th>
                                                    <th className="dy-align-center">Status</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {i.investment_form.items
                                                    .filter(item => item.proof_amount >= 0)
                                                    .map((item, index) => {
                                                        const proofStatus = getProofStatus(item.proofs);
                                                        let calculationProofObject = {
                                                            calculation_amount: item.calculation_amount,
                                                            calculation_remarks: item.calculation_remarks,
                                                            calculation_attachment: item.calculation_attachment,
                                                        }
                                                        return (

                                                            <tr key={item.id}>
                                                                <td className="align-leftt py-2 font-size-text table-item-cell"

                                                                >
                                                                    <strong style={{
                                                                        overflow: 'hidden',

                                                                    }}

                                                                        title={item.items_details?.section_name || `Section ${index + 1}`}
                                                                    >{item.items_details?.section_name || `Section ${index + 1}`}</strong>
                                                                    <br />
                                                                    <small style={{ color: '#666' }}>{item.section?.code || ''}</small>
                                                                </td>

                                                                <td className="py-2 font-size-text align-right help-sign"
                                                                    style={{
                                                                        width: '120px',
                                                                    }}
                                                                >
                                                                    <CustomTooltip
                                                                        position="top"
                                                                        tooltipContent={
                                                                            <div>
                                                                                <div>Declared Amount: <strong>{formatCurrencyIndian(Number(item.declared_amount) || 0)}</strong></div>
                                                                                <div>Final Amount: <strong>{formatCurrencyIndian(Number(item?.final_amount) || 0)}</strong></div>
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <span style={{
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',

                                                                        }}>
                                                                            {formatCurrencyIndian(Number(item?.declared_amount) || 0)}
                                                                        </span>
                                                                    </CustomTooltip>
                                                                </td>
                                                                <td className="py-2 font-size-text align-right help-sign"
                                                                    style={{
                                                                        width: '120px',
                                                                    }}>


                                                                    <CustomTooltip
                                                                        position="top"
                                                                        tooltipContent={
                                                                            <div>
                                                                                <div>Proof Amount: <strong>{formatCurrencyIndian(Number(item.proof_amount) || 0)}</strong></div>
                                                                                <div>Calculation Amount: <strong>{formatCurrencyIndian(Number(item?.calculation_amount) || 0)}</strong></div>
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <span style={{
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',

                                                                        }}>
                                                                            {formatCurrencyIndian(Number(item?.proof_amount) || 0)}
                                                                        </span>
                                                                    </CustomTooltip>
                                                                </td>

                                                                <td className="dy-align-right">
                                                                    <input
                                                                        type="number"
                                                                        value={editAmounts[item.id] ?? "0"}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;

                                                                            setEditAmounts(prev => ({
                                                                                ...prev,
                                                                                [item.id]: val === "" ? "" : Number(val)
                                                                            }));


                                                                            if (errors?.amount?.[item.id]) {
                                                                                setErrors(prev => ({
                                                                                    ...prev,
                                                                                    amount: { ...prev.amount, [item.id]: "" }
                                                                                }));
                                                                            }
                                                                        }}
                                                                        step={0.01}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        className={`form-input-tabular text-start ${errors?.amount?.[item.id] ? "error" : ""}`}
                                                                        style={{ width: "120px", borderBottom: '0.5px solid gray', margin: '5px' }}
                                                                        min={0}
                                                                        placeholder="Amount"
                                                                    />
                                                                    {errors?.amount?.[item.id] && (
                                                                        <div className="error-message font-size-text mt-1">{errors.amount[item.id]}</div>
                                                                    )}

                                                                </td>
                                                                <td className="dy-align-right">
                                                                    <button
                                                                        className="text-button allproject-dashboard"
                                                                        onClick={() => openProofModal(item.proofs)}
                                                                    >
                                                                        <BlueEye /> View
                                                                    </button>
                                                                </td>
                                                                <ProofViewModal show={viewProof} onClose={() => setViewProof(false)} proofs={selectedProofs} calculationProofsData={calculationProofObject} />
                                                                {hasManualItems && (
                                                                    <>
                                                                        <td>
                                                                            <div className="flex-column">
                                                                                <div
                                                                                    style={{ width: '10vw', marginBottom: '2px' }}
                                                                                    className={`flex-row justify-between form-input table-upload
                                                                              ${errors?.updatedProofs?.[item.id] ? "error" : ""}
                                                                              `}
                                                                                >
                                                                                    <label
                                                                                        htmlFor={`update-proof-${item.id}`}
                                                                                        style={{
                                                                                            cursor: "pointer",
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: "6px",
                                                                                            minWidth: 0
                                                                                        }}
                                                                                        title={updatedProofs?.[item.id]?.[0]?.name || "Choose file"}
                                                                                    >
                                                                                        <CloudUpload size={16} />
                                                                                        <span className="truncate-url">
                                                                                            {updatedProofs?.[item.id]?.[0]?.name || "Choose file"}
                                                                                        </span>
                                                                                    </label>

                                                                                    <input
                                                                                        type="file"
                                                                                        id={`update-proof-${item.id}`}
                                                                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpeg,.jpg"
                                                                                        style={{ display: "none" }}
                                                                                        onChange={(e) => {
                                                                                            setUpdatedProofs({
                                                                                                ...updatedProofs,
                                                                                                [item.id]: Array.from(e.target.files)
                                                                                            });
                                                                                            if (errors?.updatedProofs?.[item.id]) {
                                                                                                setErrors(prev => ({
                                                                                                    ...prev,
                                                                                                    updatedProofs: { ...prev.updatedProofs, [item.id]: "" }
                                                                                                }))
                                                                                            }
                                                                                        }}
                                                                                    />

                                                                                    {updatedProofs?.[item.id] && (
                                                                                        <span
                                                                                            className="file-clear"
                                                                                            onClick={() => {
                                                                                                const proof = { ...updatedProofs };
                                                                                                delete proof[item.id];
                                                                                                setUpdatedProofs(proof);
                                                                                            }}
                                                                                        >
                                                                                            Clear
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                {errors?.updatedProofs?.[item.id] && (
                                                                                    <div className="error-message font-size-text mt-1">
                                                                                        {errors.updatedProofs[item.id]}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                    </>
                                                                )}
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        // value={reason || item.amount_change_reason}
                                                                        // onChange={(e) => setReason(e.target.value)}
                                                                        value={reasons[item.id] || ""}
                                                                        onChange={(e) => {
                                                                            setReasons(prev => ({
                                                                                ...prev,
                                                                                [item.id]: e.target.value
                                                                            }));


                                                                            if (errors?.reason?.[item.id]) {
                                                                                setErrors(prev => ({
                                                                                    ...prev,
                                                                                    reason: { ...prev.reason, [item.id]: "" }
                                                                                }));
                                                                            }
                                                                        }}

                                                                        className={`form-input-tabular text-start ${errors?.reason?.[item.id] ? "error" : ""}`}
                                                                        placeholder='Change reason'
                                                                        style={{ width: "120px", borderBottom: '0.5px solid gray', margin: '5px' }}

                                                                    />

                                                                    {errors?.reason?.[item.id] && (
                                                                        <div className="error-message font-size-text mt-1">{errors.reason[item.id]}</div>
                                                                    )}
                                                                </td>
                                                                <td style={{ textAlign: 'center', padding: '8px' }}>
                                                                    {/* <span className={`status-badge ${proofStatus}`}>
                                                                        {proofStatus}declaration_status
                                                                    </span> */}
                                                                    <span className={`request-status text-capitalize`} request-status={item.proof_status}>
                                                                        {item.proof_status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}


                            {(isEditApproval || isReject) && (
                                <div className="mt-3 flex flex-column">
                                    <label className="form-labels font-weight500">
                                        {isReject ? "Rejection Remarks" : "Approval Remarks"}  <span className="required">*</span>
                                    </label>
                                    <textarea
                                        rows="3"
                                        name='remarks'
                                        placeholder={isReject ? "Enter reason for rejection" : "Enter remarks for amount adjustments"}
                                        className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                        value={remarks}
                                        style={{ width: 'auto' }}
                                        onChange={(e) => {
                                            setRemarks(e.target.value)
                                            if (errors.remarks) {
                                                setErrors((prev) => ({ ...prev, remarks: "" }))
                                            }
                                        }
                                        }
                                    ></textarea>
                                    {errors.remarks && (
                                        <div className="error-message">{errors.remarks}</div>
                                    )}
                                </div>
                            )}

                            {/* Display Note */}

                            {isApprove && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This approval will set all section status to <strong>approved</strong>.
                                </div>
                            )}
                            {isEditApproval && (
                                <div className='margin_top padding_ color-tab-yellow text-warning-color-dark notice-badge'>
                                    This approval will update the declared amounts and set all section status to <strong>approved</strong>.
                                </div>
                            )}
                            {isReject && (
                                <div className='margin_top padding_ color-badge-danger color-border-red notice-badge'>
                                    This rejection will set all section status to <strong>rejected</strong>. The form will need to be resubmitted, after which the approval process will restart.
                                </div>
                            )}
                            {isFinalize && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This action will finalize all sections and lock the data from further changes.
                                </div>
                            )}


                            {/* Buttons */}
                            <div className="button-models mt-4">
                                <button className="model-button model-button-cancel" onClick={handleClose}>
                                    Cancel
                                </button>

                                <button className={`model-button ${isReject ? 'model-button-delete font-weight500 font-size-heading' : 'model-button-submit'} `} onClick={handleSubmit}>
                                    {isReject
                                        ? "Reject"
                                        : isEditApproval
                                            ? "Approve with Adjustments"
                                            : isFinalize
                                                ? "Finalize"
                                                : "Approve"}
                                </button>
                            </div>

                        </div>
                    </div >
                </Modal.Body >
            </Modal >
        </>
    );
};


// Investment Proof Justification

const DynamicRequestInvestmentProofApproval = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitPut = async () => {
        const loadingToastId = toast.loading("Approving... Please wait...");
        const ApprovalFormData = {
            approval_status: "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapprovals/${i.id}/`,
                ApprovalFormData
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Investment Proof Request Approved");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Approval Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-edit-button-ml"
                title={`Investment Proof Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CheckTick />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Confirm Stage {i.level} : {i.role} Approval for{" "}<br />
                                {i?.investment_form?.emp_code}-{i?.investment_form?.emp_name}'s Investment Proofs
                            </h6>

                            <div className='flex-row justify-evenly'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof Summary:</h6>
                                    <InvestmentProofSummary form={i?.investment_form} />
                                </div>
                            </div>
                            <hr />

                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <p>{i?.investment_form?.regime_details?.name || 'Not specified'}</p>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Financial Year:</h6>
                                    <p>
                                        {i?.investment_form?.regime_details?.financial_year_start ?
                                            `${new Date(i.investment_form.regime_details.financial_year_start).getFullYear()}-${new Date(i.investment_form.regime_details.financial_year_end).getFullYear()}`
                                            : 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-leave font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve Proofs
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestInvestmentProofEditApproval = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [formData, setFormData] = useState({
        verified_amounts: {},
        remarks: ""
    });
    const [errors, setErrors] = useState({});

    const handleClose = () => setShow(false);

    const handleShow = () => {
        // Initialize verified amounts with current proof amounts
        const initialVerifiedAmounts = {};
        if (i?.investment_form?.items) {
            i.investment_form.items.forEach(item => {
                initialVerifiedAmounts[item.id] = item.proof_amount || item.declared_amount || 0;
            });
        }

        setFormData({
            verified_amounts: initialVerifiedAmounts,
            remarks: ""
        });
        setErrors({});
        setShow(true);
    };

    const handleAmountChange = (itemId, value) => {
        setFormData(prev => ({
            ...prev,
            verified_amounts: {
                ...prev.verified_amounts,
                [itemId]: parseFloat(value) || 0
            }
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.remarks) {
            newErrors.remarks = "Remarks are required!";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmitPut = async () => {
        if (!validateForm()) return;
        const loadingToastId = toast.loading("Submitting approval...");

        const ApprovalFormData = {
            approval_status: "approved",
            remarks: formData.remarks,
            updated_data: formData.verified_amounts,
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-approve-reject-edit-investment/${i.id}/`,
                ApprovalFormData
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Investment Proof Request Approved with Amount Adjustments");
                await getDynamicRequestData();
                await getApprovalStages();
                handleClose();
            } else {
                toast.error(`Approval Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button
                className="model-partial-button"
                title={`Stage ${i.level} Editable Approval`}
                onClick={handleShow}
            >
                <WarnYellow />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-60vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Verify & Approve Stage {i.level} : {i.role} Approval - Investment Proofs
                            </h6>

                            {/* Proof Summary with Editable Amounts */}
                            <div className='flex-row justify-between alignment-start'>
                                <div style={{ width: '100%' }}>
                                    <h6 className="font-weight500 mt-2">Verify Proof Amounts:</h6>
                                    <div className="proof-verification-table">
                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ textAlign: 'left', padding: '8px' }}>Section</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Declared</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Submitted Proof</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Verified Amount</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Documents</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {i?.investment_form?.items?.map((item, index) => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '8px' }}>
                                                            <strong>{item.section?.name || `Section ${index + 1}`}</strong>
                                                            <br />
                                                            <small style={{ color: '#666' }}>{item.section?.code || ''}</small>
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            ₹{parseFloat(item.declared_amount || 0).toLocaleString()}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            ₹{parseFloat(item.proof_amount || 0).toLocaleString()}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            <input
                                                                type="number"
                                                                value={formData.verified_amounts[item.id] || 0}
                                                                onChange={(e) => handleAmountChange(item.id, e.target.value)}
                                                                className="form-input-small"
                                                                style={{ width: '120px', textAlign: 'right' }}
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>
                                                            {item.proofs?.map((proof, idx) => (
                                                                <div key={proof.id} style={{ marginBottom: '4px' }}>
                                                                    <ViewFile
                                                                        doc={proof.document || proof.document_url}
                                                                        filename={`${item.section?.code || 'Sec'}-Proof-${idx + 1}`}
                                                                        filesrc={proof.document || proof.document_url}
                                                                    />
                                                                </div>
                                                            ))}
                                                            {(!item.proofs || item.proofs.length === 0) && (
                                                                <span style={{ color: '#999', fontSize: '12px' }}>No proofs</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Tax Regime Info */}
                            <div className='flex-row justify-between alignment-start mt-3'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <p>{i?.investment_form?.regime_details?.name || 'Not specified'}</p>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Financial Year:</h6>
                                    <p>
                                        {i?.investment_form?.regime_details?.financial_year_start ?
                                            `${new Date(i.investment_form.regime_details.financial_year_start).getFullYear()}-${new Date(i.investment_form.regime_details.financial_year_end).getFullYear()}`
                                            : 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            {/* Remarks */}
                            <div className="flex-column mt-3">
                                <label className="form-labels font-weight500">
                                    Verification Remarks <span className="required">*</span>
                                </label>
                                <textarea
                                    name="remarks"
                                    placeholder="Enter verification remarks and reason for amount adjustments"
                                    onChange={handleInputChange}
                                    value={formData.remarks}
                                    className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                    rows="3"
                                />
                                {errors.remarks && (
                                    <span className="error-message">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-submit"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve with Adjustments
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestInvestmentProofFinalize = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [formData, setFormData] = useState({
        final_amounts: {},
        remarks: ""
    });

    const handleClose = () => setShow(false);
    const handleShow = () => {
        // Initialize final amounts with current verified amounts
        const initialFinalAmounts = {};
        if (i?.investment_form?.items) {
            i.investment_form.items.forEach(item => {
                initialFinalAmounts[item.id] = {
                    verified_amount: item.proof_amount || item.declared_amount || 0,
                    status: item.proof_status === 'verified' ? 'approved' : 'pending'
                };
            });
        }

        setFormData({
            final_amounts: initialFinalAmounts,
            remarks: ""
        });
        setShow(true);
    };

    const handleFinalAmountChange = (itemId, value) => {
        setFormData(prev => ({
            ...prev,
            final_amounts: {
                ...prev.final_amounts,
                [itemId]: {
                    ...prev.final_amounts[itemId],
                    verified_amount: parseFloat(value) || 0
                }
            }
        }));
    };

    const handleStatusChange = (itemId, status) => {
        setFormData(prev => ({
            ...prev,
            final_amounts: {
                ...prev.final_amounts,
                [itemId]: {
                    ...prev.final_amounts[itemId],
                    status: status
                }
            }
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFormSubmitPut = async () => {
        const loadingToastId = toast.loading("Finalizing Investment Proofs...");

        const ApprovalFormData = {
            approval_status: "approved",
            remarks: formData.remarks,
            updated_data: formData.final_amounts,
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            const req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-finalize-investment/${i.id}/`,
                ApprovalFormData,
            );

            if (req.status === 200 || req.status === 201) {
                toast.success("Investment Proofs Finalized Successfully");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Finalize Error: ${req.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-edit-button-ml"
                title={`Finalize Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CheckTick />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-70vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Finalize Stage {i.level} : {i.role} Approval for <br />
                                {i?.investment_form?.emp_code}-{i?.investment_form?.emp_name}'s Investment Proofs
                            </h6>

                            <div className="flex-row justify-evenly">
                                <div style={{ width: '100%' }}>
                                    <h6 className="font-weight500 mt-2">Final Proof Verification:</h6>
                                    <div className="proof-finalization-table">
                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ textAlign: 'left', padding: '8px' }}>Section</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Declared</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Submitted Proof</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Final Approved Amount</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Final Status</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Documents</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {i?.investment_form?.items?.map((item, index) => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '8px' }}>
                                                            <strong>{item.section?.name || `Section ${index + 1}`}</strong>
                                                            <br />
                                                            <small style={{ color: '#666' }}>{item.section?.code || ''}</small>
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            ₹{parseFloat(item.declared_amount || 0).toLocaleString()}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            ₹{parseFloat(item.proof_amount || 0).toLocaleString()}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>
                                                            <input
                                                                type="number"
                                                                value={formData.final_amounts[item.id]?.verified_amount || 0}
                                                                onChange={(e) => handleFinalAmountChange(item.id, e.target.value)}
                                                                className="form-input-small"
                                                                style={{ width: '120px', textAlign: 'right' }}
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>
                                                            <select
                                                                value={formData.final_amounts[item.id]?.status || 'pending'}
                                                                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                                                className="form-input-small"
                                                                style={{ width: '120px' }}
                                                            >
                                                                <option value="approved">Approved</option>
                                                                <option value="rejected">Rejected</option>
                                                                <option value="pending">Pending</option>
                                                            </select>
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>
                                                            {item.proofs?.map((proof, idx) => (
                                                                <div key={proof.id} style={{ marginBottom: '4px' }}>
                                                                    <ViewFile
                                                                        doc={proof.document || proof.document_url}
                                                                        filename={`${item.section?.code || 'Sec'}-Proof-${idx + 1}`}
                                                                        filesrc={proof.document || proof.document_url}
                                                                    />
                                                                </div>
                                                            ))}
                                                            {(!item.proofs || item.proofs.length === 0) && (
                                                                <span style={{ color: '#999', fontSize: '12px' }}>No proofs</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <hr />

                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <p>{i?.investment_form?.regime_details?.name || 'Not specified'}</p>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Financial Year:</h6>
                                    <p>
                                        {i?.investment_form?.regime_details?.financial_year_start ?
                                            `${new Date(i.investment_form.regime_details.financial_year_start).getFullYear()}-${new Date(i.investment_form.regime_details.financial_year_end).getFullYear()}`
                                            : 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            {/* Final Remarks */}
                            <div className="flex-column mt-3">
                                <label className="form-labels font-weight500">
                                    Final Remarks
                                </label>
                                <textarea
                                    name="remarks"
                                    placeholder="Enter final remarks for proof verification"
                                    onChange={handleInputChange}
                                    value={formData.remarks}
                                    className="form-input-textarea"
                                    rows="3"
                                />
                            </div>

                            <div className="button-models mt-4">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="model-button model-button-leave font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Finalize Proofs
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DynamicRequestInvestmentProofRejection = ({ i, getDynamicRequestData, getApprovalStages }) => {
    const [show, setShow] = useState(false);
    const [formData, setFormData] = useState({ remarks: "" });
    const [errors, setErrors] = useState({});

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({ remarks: "" });
        setErrors({});
        setShow(true);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.remarks.trim()) {
            newErrors.remarks = "Remarks are required!";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const loadingToastId = toast.loading("Rejecting... Please wait...");

        const RejectedStatus = {
            approval_status: "rejected",
            remarks: formData.remarks.trim(),
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code"),
        };

        try {
            let req = await axios.put(
                `${BASE_URL}/wfm/dynamicapproval-stagesupdate-customreject-investment/${i.id}/`,
                RejectedStatus
            );
            if (req.status === 200 || req.status === 201) {
                toast.success("Investment Proof Request Rejected");
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${req.statusText}`);
                console.error(req);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            toast.dismiss(loadingToastId);
            handleClose();
        }
    };

    return (
        <>
            <button
                className="model-delete-button"
                title={`Reject Stage ${i.level} Approval`}
                onClick={handleShow}
            >
                <CrossRed />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Reject Stage {i.level} : {i.role} Approval for{" "}
                                <br />
                                {i?.investment_form?.emp_code} - {i?.investment_form?.emp_name}'s Investment Proofs
                            </h6>

                            <div className="flex-row justify-evenly">
                                <div>
                                    <h6 className="font-weight500 mt-2">Proof Summary:</h6>
                                    <InvestmentProofSummary form={i?.investment_form} />
                                </div>
                            </div>
                            <hr />

                            <div className='flex-row justify-between alignment-start'>
                                <div>
                                    <h6 className="font-weight500 mt-2">Tax Regime:</h6>
                                    <p>{i?.investment_form?.regime_details?.name || 'Not specified'}</p>
                                </div>
                                <div>
                                    <h6 className="font-weight500 mt-2">Financial Year:</h6>
                                    <p>
                                        {i?.investment_form?.regime_details?.financial_year_start ?
                                            `${new Date(i.investment_form.regime_details.financial_year_start).getFullYear()}-${new Date(i.investment_form.regime_details.financial_year_end).getFullYear()}`
                                            : 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex-column mt-3">
                                <label htmlFor="remarks" className="form-labels font-weight500">
                                    Rejection Remarks <span className="required">*</span>
                                </label>
                                <textarea
                                    id="remarks"
                                    name="remarks"
                                    placeholder="Enter detailed reason for proof rejection"
                                    onChange={(e) =>
                                        setFormData({ ...formData, remarks: e.target.value })
                                    }
                                    value={formData.remarks}
                                    className={`form-input-textarea ${errors.remarks ? "error" : formData.remarks ? "success" : ""
                                        }`}
                                    rows="4"
                                />
                                {errors.remarks && (
                                    <span className="error-message">{errors.remarks}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button
                                    className="model-button model-button-cancel"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button model-button-delete font-weight500 font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Reject Proofs
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const BankSummary = ({ i }) => {
    if (!i?.bank_details) return <div>No Bank data available</div>;

    const {
        previous_data,
        updated_data,
        changed_fields,
        proof_attachment,
        reason_for_change,
    } = i?.bank_details;

    return (
        <div className="inv-summary-card">

            {/* Current vs Requested */}
            <div className="flex-row justify-evenly">
                <div>
                    <h6 className="font-weight500 mt-2">Current Details:</h6>
                    <table>
                        <tbody>
                            {[
                                { label: "Account No", value: previous_data?.account_number },
                                { label: "IFSC", value: previous_data?.ifsc_code },
                                { label: "Bank", value: previous_data?.bank_name },
                                { label: "Branch", value: previous_data?.branch_address },
                            ].map(({ label, value }) => (
                                <tr key={label}>
                                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                    <td style={{ padding: "4px 8px" }}>:</td>
                                    <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className='vertical-divider' />
                <div>
                    <h6 className="font-weight500 mt-2">Requested Updates:</h6>
                    <table>
                        <tbody>
                            {[
                                { label: "Account No", value: updated_data?.account_number },
                                { label: "IFSC", value: updated_data?.ifsc_code },
                                { label: "Bank", value: updated_data?.bank_name },
                                { label: "Branch", value: updated_data?.branch_address },
                            ].map(({ label, value }) => (
                                <tr key={label}>
                                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                    <td style={{ padding: "4px 8px" }}>:</td>
                                    <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <hr />

            {/* Changed fields + Proof */}
            <div className="flex-row justify-between alignment-start">
                <div>
                    <h6 className="font-weight500 mt-2">Changed Fields:</h6>
                    {changed_fields?.length > 0 ? (
                        <ul>
                            {
                                changed_fields.map((f, idx) => <li key={idx}>{f}</li>)
                            }
                        </ul>
                    ) :
                        (
                            '-'
                        )
                    }

                </div>

                <div>
                    <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                    {proof_attachment ? (
                        <ViewFile
                            doc={proof_attachment}
                            filename="Proof Attachment"
                            filesrc={proof_attachment}
                        />
                    ) : (
                        "No File Attached"
                    )}
                </div>
            </div>


            <div className="flex-row emp_email_u_body">
                <h6 className="font-weight500 mt-2">Reason:</h6>
                <p>{reason_for_change || "-"}</p>
            </div>
        </div>
    );
};


const ArrearSummary = ({ form }) => {

    if (!form) return <div>No arrear data available</div>;

    const {
        amount,
        date_list,
        days_calculated,
        start_date,
        end_date,
        approved_amount,
        approved_days,
        status,
        nature_name,
    } = form;

    const getNatureType = () => {
        if (date_list?.length > 0) return "Days";
        if (start_date && end_date) return "Month";
        if (amount) return "Amount";
        return "-";
    };

    const natureType = getNatureType();

    return (
        <div className="inv-summary-card">

            <table className="inv-table">
                <thead>
                    <tr>
                        <th className="align-leftt font-size-text">Nature</th>
                        <th className="dy-align-center font-size-text">Value</th>
                        {(nature_name === "Days" || nature_name === "Month") && (
                            <th className="dy-align-center font-size-text">Approved Days</th>
                        )}
                        {nature_name === "Amount" && (
                            <th className="dy-align-center font-size-text">Approved Amount</th>
                        )}
                        <th className="align-left font-size-text">Status</th>
                    </tr>
                </thead>

                <tbody>

                    <td className="align-leftt py-2 font-size-text table-item-cell">{natureType}</td>
                    <td
                        style={{ cursor: 'help' }}
                        className="dy-align-center py-2 font-size-text table-item-cell table-item-small-cell"
                        title={days_calculated ? `No of days: ${days_calculated}` : ""}
                    >
                        {/* {natureType === "Days" && date_list?.map((date) => formatDate(date)).join(", ")} */}
                        {natureType === "Days" && date_list?.length > 0 && (() => {
                            const formattedDates = date_list.map(date => formatDate(date));

                            const visibleDates = formattedDates.slice(0, 3).join(", ");
                            const fullDates = formattedDates.join(", ");

                            return (
                                <span title={formattedDates.length > 3 ? fullDates : ""}>
                                    {formattedDates.length > 3
                                        ? `${visibleDates}...`
                                        : fullDates}
                                </span>
                            );
                        })()}

                        {natureType === "Month" && `${formatDate(start_date)} to ${formatDate(end_date)}`}
                        {natureType === "Amount" && formatCurrencyIndian(amount)}
                    </td>

                    {(nature_name === "Days" || nature_name === "Month") && (
                        <td className="align-center py-2 font-size-text">{approved_days || '-'}</td>
                    )}
                    {nature_name === "Amount" && (
                        <td className="align-center py-2 font-size-text">{formatCurrencyIndian(approved_amount) || "-"}</td>
                    )}
                    <td className="align-center py-2 font-size-text">
                        <span request-status={status} className='request-status text-capitalize'>
                            {status}
                        </span>
                    </td>
                </tbody>
            </table>
        </div>
    );
};


const CompensatoryLeaveSummary = ({ form }) => {

    if (!form) return <div>No Compensatory req data available</div>;

    const {
        date,
        in_time,
        out_time,
        work_duration,
        duration,
        approval_flow_version,
        instructed_by,
        reason,
        dynamic_approval,
    } = form;


    return (
        <div className="inv-summary-card">

            <table className="table-css">

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Date
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right">
                        {formattedDateLong(date)}
                    </td>
                </tr>

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        In-Time
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right">
                        {in_time}
                    </td>
                </tr>

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Out-Time
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right">
                        {out_time}
                    </td>
                </tr>

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Working Hours
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right">
                        {work_duration}
                    </td>
                </tr>

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Compensatory Type
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right  text-capitalize">
                        {duration}
                    </td>
                </tr>
                {approval_flow_version > 0 && <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Instructed By
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right  text-capitalize">
                        {instructed_by}
                    </td>
                </tr>}
                {approval_flow_version > 0 && <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Reason
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right  text-capitalize">
                        {reason}
                    </td>
                </tr>}

                <tr>
                    <td className="table-body" style={{ paddingBottom: "5px" }}>
                        Leave To be Added
                    </td>
                    <td style={{ padding: "0px 4px" }}>:</td>
                    <td className="align-right">
                        {duration === 'short leave'
                            ?
                            // ((Array.isArray(dynamic_approval) && dynamic_approval.length === 0) && approval_flow_version === 0)
                            // (Incomplete) After confirmation if all older raised req add leaves in older format only
                            (approval_flow_version === 0)
                                ?
                                '0.25 to Previous Leave'
                                :
                                '1 to Short Leave'
                            :
                            '1 to Earn Leave'}
                    </td>
                </tr>
            </table>
        </div>
    );
};


const CompensatoryLeaveSummaryTable = ({ form }) => {

    if (!form) return <div>No Compensatory req data available</div>;

    const {
        date,
        in_time,
        out_time,
        work_duration,
        duration,
        rh_leave_status,
    } = form;


    return (
        <div className="inv-summary-card">

            <table className="inv-table">
                <thead>
                    <tr>
                        <th className="align-center">Date</th>
                        <th className="align-center">In-Time</th>
                        <th className="align-center">Out-Time</th>
                        <th className="align-center">Working Hours</th>
                        <th className="align-center">Compensatory Type</th>
                        <th className="align-center">Status</th>
                    </tr>
                </thead>

                <tbody>
                    <td className="align-center">
                        {formattedDateLong(date)}
                    </td>
                    <td className="align-center">{in_time}</td>
                    <td className="align-center">{out_time}</td>
                    <td className="align-center">{work_duration}</td>
                    <td className="align-center text-capitalize">
                        {duration}
                    </td>
                    <td className="align-center py-2 font-size-text">
                        <span request-status={rh_leave_status} className='request-status text-capitalize'>
                            {rh_leave_status}
                        </span>
                    </td>
                </tbody>
            </table>
        </div>
    );
};




const ArrearDynamicApprovalActionModal = ({
    i,
    approvalType,
    getDynamicRequestData,
    getApprovalStages
}) => {

    const permission = i?.permissions;

    const PERMISSIONS = {
        APPROVE_REJECT: "Approve-Reject", // Approve, Reject
        APPROVE_REJECT_EDIT: "Approve-Reject-Edit", //Approve, Edit-Approve, Reject
        FINALIZE: "Finalize", // Finalize, Reject
        FINALIZE_EDIT: "Finalize-Edit", //Finalize, Edit-Finalize, Reject
    };

    const actionConfig = {
        [PERMISSIONS.APPROVE_REJECT]: ["approve", "reject"],
        [PERMISSIONS.APPROVE_REJECT_EDIT]: ["approve", "edit", "reject"],
        [PERMISSIONS.FINALIZE]: ["finalize", "reject"],
        [PERMISSIONS.FINALIZE_EDIT]: ["finalize", "edit", "reject"],
    };

    const allowedActions = actionConfig[permission] || [];

    const [action, setAction] = useState(null);

    const [show, setShow] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [errors, setErrors] = useState({});
    const [editAmounts, setEditAmounts] = useState({});

    const isReject = action === "reject";
    const isEditApproval = action === "edit";
    const isFinalize = action === "finalize";
    const isApprove = action === "approve";

    const requiresRemarks = isReject || isEditApproval;

    const handleShow = () => {
        if (isEditApproval) {
            setEditAmounts({
                [i?.arrear_details?.id]: i?.arrear_details?.approved_amount ??
                    i?.arrear_details?.amount ??
                    0
            });
        }

        setRemarks("");
        setErrors({});
        setShow(true);
    };


    const handleClose = () => setShow(false);

    const validateForm = () => {
        const err = {};
        if (requiresRemarks && !remarks.trim()) {
            err.remarks = "Remarks are required!";
        }
        if (isEditApproval && nature === "Amount") {
            const arrearId = i?.arrear_details?.id;
            const amount =
                editAmounts[arrearId] ?? i?.arrear_details?.amount;

            if (amount === "" || amount === null || amount === undefined) {
                err.amount = "Amount is required!";
            } else if (Number(amount) <= 0) {
                err.amount = "Amount must be greater than 0!";
            }
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const API_URL = `${BASE_URL}/wfm/dynamicapproval-arrear-all-approval/${i.id}/`


    const handleSubmit = async () => {
        if (!validateForm()) return;

        const loadingToast = toast.loading("Processing...");

        let payload = {
            approval_status: isReject ? "rejected" : "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code")
        };


        if (isReject) {
            payload.remarks = remarks.trim();
        }

        if (isEditApproval) {

            const newValue =
                editAmounts[i?.arrear_details?.id] ??
                i?.arrear_details?.amount;

            payload.remarks = remarks.trim();

            payload.request_update_data = {
                id: i?.arrear_details?.id,
                approved_amount: Number(newValue)
            };
        }

        try {
            const res = await axios.put(API_URL, payload);

            if (res.status === 200 || res.status === 201) {
                toast.success(
                    isReject
                        ? "Request Rejected"
                        : isFinalize
                            ? "Request Finalized"
                            : isEditApproval
                                ? "Approved with Adjustments"
                                : "Request Approved"
                );
                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${res.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToast);
        } finally {
            toast.dismiss(loadingToast);
            handleClose();
        }
    };

    const summaryComponentMap = {
        arrear: ArrearSummary,
    };

    const SummaryComponent =
        summaryComponentMap[approvalType] || '';


    const actionButtonConfig = {
        approve: {
            title: "Approve",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        edit: {
            title: "Edit & Approve",
            className: "model-partial-button",
            icon: <WarnYellow />,
        },
        finalize: {
            title: "Finalize",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        reject: {
            title: "Reject",
            className: "model-delete-button",
            icon: <CrossRed />,
        },
    };


    const getNatureType = () => {
        if (i?.arrear_details?.date_list?.length > 0) return "Days";
        if (i?.arrear_details?.start_date && i?.arrear_details?.end_date) return "Month";
        if (i?.arrear_details?.amount) return "Amount";
        return "-";
    };

    const natureType = getNatureType();
    const arrearId = i?.arrear_details?.id;
    const currentValue = editAmounts[arrearId] ?? i?.arrear_details?.amount;
    const nature = i?.arrear_details?.nature_name;


    return (
        <>
            {/* <button
                className={buttonConfig[type].className}
                title={buttonConfig[type].title}
                onClick={handleShow}
            >
                {buttonConfig[type].icon}
            </button> */}

            {allowedActions.map((actionType) => {
                const config = actionButtonConfig[actionType];
                if (!config) return null;

                return (
                    <button
                        key={actionType}
                        className={config.className}
                        title={config.title}
                        onClick={() => {
                            setAction(actionType);
                            handleShow();
                        }}
                    >
                        {config.icon}
                    </button>
                );
            })}

            <Modal show={show} onHide={handleClose} dialogClassName={action === "edit" ? "three-fourth-modal" : "custom-modal"}>
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">

                            {/* Heading section */}
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                {isApprove && (
                                    <>
                                        {`Confirm Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.arrear_details.emp_code}-${i.arrear_details.emp_name}'s Arrear`}
                                    </>
                                )}
                                {isEditApproval && (
                                    <>
                                        {`Edit & Approve Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.arrear_details.emp_code}-${i.arrear_details.emp_name}'s Arrear`}
                                    </>

                                )}
                                {isReject && (
                                    <>
                                        {`Reject Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.arrear_details.emp_code}-${i.arrear_details.emp_name}'s Arrear`}
                                    </>
                                )}
                                {isFinalize && (
                                    <>
                                        {`Finalize Stage ${i.level} : ${i.role}`}  Approval for
                                        <br />
                                        {`${i.arrear_details.emp_code}-${i.arrear_details.emp_name}'s Arrear`}
                                    </>
                                )}

                            </h6>




                            {/* Arrear Summary */}

                            <div className="flex-column justify-evenly mt-2 align-center">

                                <h6 className="font-weight500  mb-3">
                                    {isReject
                                        ? "Arrear Summary:"
                                        : isFinalize
                                            ?
                                            "Arrear Summary:"
                                            : isEditApproval
                                                ? "Adjust Arrear Amounts:"
                                                : "Arrear Details:"}

                                </h6>
                                {!isEditApproval && <SummaryComponent form={i.arrear_details} />}
                            </div>


                            {/* Arrear summary table  for Edit */}
                            {isEditApproval && (
                                <div>

                                    <div className='table-css-white-background scroll-container-table' style={{ minHeight: '0px' }}>
                                        <table className="inv-table table-css">
                                            <thead>
                                                <tr>
                                                    <th className="align-leftt font-size-text">Nature</th>
                                                    <th className="dy-align-center font-size-text">Value</th>
                                                    {(nature === "Days" || nature === "Month") && (
                                                        <th className="dy-align-center font-size-text">Approved Days</th>
                                                    )}

                                                    {nature === "Amount" && (
                                                        <>
                                                            <th className="dy-align-center font-size-text">Approved Amount</th>
                                                            <th className="dy-align-center font-size-text">Final Amount</th>
                                                        </>
                                                    )}


                                                    <th className="align-left font-size-text">Status</th>
                                                </tr>
                                            </thead>

                                            <tbody>

                                                <td className="align-leftt py-2 font-size-text table-item-cell">{i?.arrear_details?.nature_name}</td>
                                                <td
                                                    style={{ cursor: 'help' }}
                                                    className="dy-align-center py-2 font-size-text table-item-cell table-item-small-cell"
                                                    title={i.arrear_details.days_calculated ? `No of days: ${i.arrear_details.days_calculated}` : ""}
                                                >
                                                    {natureType === "Days" && i?.arrear_details?.date_list?.length > 0 && (() => {
                                                        const formattedDates = i?.arrear_details?.date_list.map(date => formatDate(date));

                                                        const visibleDates = formattedDates.slice(0, 3).join(", ");
                                                        const fullDates = formattedDates.join(", ");

                                                        return (
                                                            <span title={formattedDates.length > 3 ? fullDates : ""}>
                                                                {formattedDates.length > 3
                                                                    ? `${visibleDates}...`
                                                                    : fullDates}
                                                            </span>
                                                        );
                                                    })()}

                                                    {/* {natureType === "Days" && i?.arrear_details?.date_list?.map((date) => formatDate(date)).join(", ")} */}
                                                    {natureType === "Month" && `${formatDate(i?.arrear_details?.start_date)} to ${formatDate(i?.arrear_details?.end_date)}`}
                                                    {natureType === "Amount" && formatCurrencyIndian(i?.arrear_details?.amount)}
                                                </td>


                                                {/* <td className="align-center py-2 font-size-text">{i?.arrear_details?.approved_days || '-'}</td>

                                                <td className="align-center py-2 font-size-text">{formatCurrencyIndian(i?.arrear_details?.approved_amount) || "-"}</td> */}
                                                {(nature === "Days" || nature === "Month") && (
                                                    <td className="align-center py-2 font-size-text">
                                                        {i?.arrear_details?.approved_days || '-'}
                                                    </td>
                                                )}

                                                {nature === "Amount" && (
                                                    <>
                                                        <td className="align-center py-2 font-size-text">
                                                            {i?.arrear_details?.approved_amount
                                                                ? formatCurrencyIndian(i?.arrear_details?.approved_amount)
                                                                : "-"
                                                            }
                                                        </td>
                                                        <td className="dy-align-right">
                                                            <input
                                                                type="number"

                                                                value={editAmounts[i?.arrear_details?.id] === undefined ? i?.arrear_details?.amount : editAmounts[i?.arrear_details?.id]}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setEditAmounts(prev => ({
                                                                        ...prev,
                                                                        [i?.arrear_details?.id]: val === "" ? "" : Number(val)
                                                                    }))
                                                                    if (errors.amount) {
                                                                        setErrors(prev => ({ ...prev, amount: "" }));
                                                                    }
                                                                }
                                                                }
                                                                step={0.01}
                                                                onWheel={(e) => e.target.blur()}
                                                                className={`form-input-tabular text-start ${errors.amount ? "error" : ""}`}
                                                                style={{ width: "120px", borderBottom: '0.5px solid gray', margin: '5px' }}
                                                                min={0}
                                                                placeholder='Amount'
                                                            />
                                                            {errors.amount && (
                                                                <div className="error-message font-size-text mt-1">{errors.amount}</div>
                                                            )}
                                                        </td>
                                                    </>
                                                )}



                                                <td className="align-center py-2 font-size-text">
                                                    <span request-status={i?.arrear_details?.status} className='request-status text-capitalize'>
                                                        {i?.arrear_details?.status}
                                                    </span>
                                                </td>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}


                            {(isEditApproval || isReject) && (
                                <div className="mt-3 flex flex-column">
                                    <label className="form-labels font-weight500">
                                        {isReject ? "Rejection Remarks" : "Approval Remarks"}  <span className="required">*</span>
                                    </label>
                                    <textarea
                                        rows="3"
                                        name='remarks'
                                        placeholder={isReject ? "Enter reason for rejection" : "Enter remarks for amount adjustments"}
                                        className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                        value={remarks}
                                        style={{ width: 'auto' }}
                                        onChange={(e) => {
                                            setRemarks(e.target.value)
                                            if (errors.remarks) {
                                                setErrors(prev => ({ ...prev, remarks: "" }));
                                            }
                                        }}
                                    ></textarea>
                                    {errors.remarks && (
                                        <div className="error-message">{errors.remarks}</div>
                                    )}
                                </div>
                            )}

                            {/* Display Note */}

                            {isApprove && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This approval will set arrear status to <strong>approved</strong>.
                                </div>
                            )}
                            {isEditApproval && (
                                <div className='margin_top padding_ color-tab-yellow text-warning-color-dark notice-badge'>
                                    This approval will update the arrear amounts and set arrear status to <strong>approved</strong>.
                                </div>
                            )}
                            {isReject && (
                                <div className='margin_top padding_ color-badge-danger color-border-red notice-badge'>
                                    This rejection will set arrear status to <strong>rejected</strong>. The form will need to be resubmitted, after which the approval process will restart.
                                </div>
                            )}
                            {isFinalize && (
                                <div className='margin_top padding_ color-badge-success color-border-green notice-badge'>
                                    This action will finalize arrear and lock the data from further changes.
                                </div>
                            )}


                            {/* Buttons */}
                            <div className="button-models mt-4">
                                <button className="model-button model-button-cancel" onClick={handleClose}>
                                    Cancel
                                </button>

                                <button className={`model-button ${isReject ? 'model-button-delete font-weight500 font-size-heading' : 'model-button-submit'} `} onClick={handleSubmit}>
                                    {isReject
                                        ? "Reject"
                                        : isEditApproval
                                            ? "Approve with Adjustments"
                                            : isFinalize
                                                ? "Finalize"
                                                : "Approve"}
                                </button>
                            </div>

                        </div>
                    </div >
                </Modal.Body >
            </Modal >
        </>
    );
};

const BankDetailsDynamicApprovalActionModal = ({
    i,
    approvalType,
    getDynamicRequestData,
    getApprovalStages
}) => {

    const permission = i?.permissions;

    const PERMISSIONS = {
        APPROVE_REJECT: "Approve-Reject", // Approve, Reject
        APPROVE_REJECT_EDIT: "Approve-Reject-Edit", //Approve, Edit-Approve, Reject
        FINALIZE: "Finalize", // Finalize, Reject
        FINALIZE_EDIT: "Finalize-Edit", //Finalize, Edit-Finalize, Reject
    };

    const actionConfig = {
        [PERMISSIONS.APPROVE_REJECT]: ["approve", "reject"],
        [PERMISSIONS.APPROVE_REJECT_EDIT]: ["approve", "edit", "reject"],
        [PERMISSIONS.FINALIZE]: ["finalize", "reject"],
        [PERMISSIONS.FINALIZE_EDIT]: ["finalize", "edit", "reject"],
    };

    const allowedActions = actionConfig[permission] || [];

    const [action, setAction] = useState(null);

    const [show, setShow] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        account_number: "",
        ifsc_code: "",
        bank_name: "",
        branch_address: "",

    });

    const isReject = action === "reject";
    const isEditApproval = action === "edit";
    const isFinalize = action === "finalize";
    const isApprove = action === "approve";

    const requiresRemarks = isReject || isEditApproval;

    const handleShow = (actionType) => {
        setAction(actionType);
        if (actionType === "edit") {

            setFormData({
                account_number: i?.bank_details?.updated_data?.account_number || "",
                ifsc_code: i?.bank_details?.updated_data?.ifsc_code || "",
                bank_name: i?.bank_details?.updated_data?.bank_name || "",
                branch_address: i?.bank_details?.updated_data?.branch_address || "",
            });
        }

        setRemarks("");
        setErrors({});
        setShow(true);
    };


    const handleClose = () => setShow(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };


    const validateForm = () => {
        const err = {};
        if (requiresRemarks && !remarks.trim()) {
            err.remarks = "Remarks are required!";
        }
        if (isEditApproval) {
            ["account_number", "ifsc_code", "bank_name", "branch_address"].forEach((field) => {
                if (!formData[field]) {
                    err[field] = `${field.replace(/_/g, " ")} is required!`;
                }
            });
        }
        if (isEditApproval) {
            const baseData =
                i?.bank_details?.updated_data ||
                i?.bank_details?.previous_data ||
                {};

            const changed = Object.keys(formData).filter(
                (key) => formData[key] !== baseData[key]
            );

            if (changed.length === 0) {
                err.form = "No changes detected to approve.";
            }
        }


        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const API_URL = `${BASE_URL}/wfm/dynamicapproval-bankdetails-all-approval/${i.id}/`


    const handleSubmit = async () => {
        if (!validateForm()) return;

        const loadingToast = toast.loading("Processing...");
        const prev = i?.bank_details?.previous_data || {};

        const baseData =
            i?.bank_details?.updated_data ||
            i?.bank_details?.previous_data ||
            {};

        const changedFields = Object.keys(formData).filter(
            (key) =>
                key !== "remarks" &&
                formData[key] !== baseData[key]
        );



        let payload = {
            approval_status: isReject ? "rejected" : "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code")
        };

        if (requiresRemarks) {
            payload.remarks = remarks.trim();
        }

        if (isEditApproval) {
            payload.updated_data = {
                account_number: formData.account_number,
                ifsc_code: formData.ifsc_code,
                bank_name: formData.bank_name,
                branch_address: formData.branch_address,
            };
            payload.changed_fields = changedFields;
        }

        try {
            const res = await axios.put(API_URL, payload);

            if (res.status === 200 || res.status === 201) {

                toast.success(
                    isReject
                        ? "Request Rejected"
                        : isFinalize
                            ? "Request Finalized"
                            : isEditApproval
                                ? "Approved with Adjustments"
                                : "Request Approved"
                );

                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${res.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToast);
        } finally {
            toast.dismiss(loadingToast);
            handleClose();
        }
    };

    const summaryComponentMap = {
        bankdetails: BankSummary,
    };

    const SummaryComponent =
        summaryComponentMap[approvalType] || '';


    const actionButtonConfig = {
        approve: {
            title: "Approve",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        edit: {
            title: "Edit & Approve",
            className: "model-partial-button",
            icon: <WarnYellow />,
        },
        finalize: {
            title: "Finalize",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        reject: {
            title: "Reject",
            className: "model-delete-button",
            icon: <CrossRed />,
        },
    };


    return (
        <>

            {allowedActions.map((actionType) => {
                const config = actionButtonConfig[actionType];
                if (!config) return null;

                return (
                    <button
                        key={actionType}
                        className={config.className}
                        title={config.title}
                        onClick={() => {
                            // setAction(actionType);
                            handleShow(actionType);
                        }}
                    >
                        {config.icon}
                    </button>
                );
            })}

            <Modal show={show} onHide={handleClose} dialogClassName="custom-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">

                            {/* Heading section */}
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                {isApprove && (
                                    <>
                                        {`Confirm Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.bank_details.emp_code}-${i.bank_details.emp_name}'s Bank Details Change`}
                                    </>
                                )}
                                {isEditApproval && (
                                    <>
                                        {`Edit & Approve Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.bank_details.emp_code}-${i.bank_details.emp_name}'s Bank Details Change`}
                                    </>

                                )}
                                {isReject && (
                                    <>
                                        {`Reject Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.bank_details.emp_code}-${i.bank_details.emp_name}'s Bank Details Change`}
                                    </>
                                )}
                                {isFinalize && (
                                    <>
                                        {`Finalize Stage ${i.level} : ${i.role}`}  Approval for
                                        <br />
                                        {`${i.bank_details.emp_code}-${i.bank_details.emp_name}'s Bank Details Change`}
                                    </>
                                )}

                            </h6>


                            {!isEditApproval && <SummaryComponent i={i} />}

                            {/* Arrear summary table  for Edit */}
                            {isEditApproval && (
                                <div className="inv-summary-card">

                                    {/* Current Details */}
                                    <div className='flex-row justify-between alignment-start'>
                                        <div>
                                            <h6 className="font-weight500 mt-2">Current Details:</h6>
                                            <table>
                                                <tbody>
                                                    {[
                                                        { label: "Account No", value: i?.bank_details?.previous_data?.account_number },
                                                        { label: "IFSC", value: i?.bank_details?.previous_data?.ifsc_code },
                                                        { label: "Bank", value: i?.bank_details?.previous_data?.bank_name },
                                                        { label: "Branch", value: i?.bank_details?.previous_data?.branch_address }
                                                    ].map(({ label, value }) => (
                                                        <tr key={label}>
                                                            <td style={{ padding: "4px 8px", fontWeight: 500 }}>{label}</td>
                                                            <td style={{ padding: "4px 8px" }}>:</td>
                                                            <td style={{ padding: "4px 8px" }}>{value || "-"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className='vertical-divider' />
                                        <div>
                                            <h6 className="font-weight500 mt-2">Proof for Changes:</h6>
                                            {i?.bank_details.proof_attachment ? (
                                                <ViewFile
                                                    doc={i?.bank_details.proof_attachment}
                                                    filename={"Proof Attachment"}
                                                    filesrc={i?.bank_details.proof_attachment}
                                                />
                                            ) : (
                                                'No File Attached'
                                            )}
                                        </div>

                                    </div>
                                    <div className='flex-row emp_email_u_body'>
                                        <h6 className="font-weight500 mt-2">Reason:</h6>
                                        <p className=''>{i?.bank_details?.reason_for_change || '-'}</p>
                                    </div>
                                    <hr />

                                    {/* Editable Updates */}
                                    <div>
                                        <h6 className="font-weight500 mt-2">Edit Requested Updates:</h6>
                                        {["account_number", "ifsc_code", "bank_name", "branch_address"].map((field) => (
                                            <div key={field} className="flex-column mb-2">
                                                <label className="form-labels font-weight500">
                                                    {field.replace(/_/g, " ").toUpperCase()}
                                                </label>
                                                <input
                                                    name={field}
                                                    value={formData[field]}
                                                    onChange={handleInputChange}
                                                    className={`form-input ${errors[field] ? "error" : ""}`}
                                                />
                                                {errors[field] && (
                                                    <span className="error-message">{errors[field]}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(isEditApproval || isReject) && (
                                <div className="mt-3 flex flex-column">
                                    <label className="form-labels font-weight500">
                                        {isReject ? "Rejection Remarks" : "Remarks"}  <span className="required">*</span>
                                    </label>
                                    <textarea
                                        rows="3"
                                        name='remarks'
                                        placeholder={isReject ? "Enter reason for rejection" : "Enter remarks for amount adjustments"}
                                        className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                        value={remarks}
                                        style={{ width: 'auto' }}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    ></textarea>
                                    {errors.remarks && (
                                        <div className="error-message">{errors.remarks}</div>
                                    )}
                                </div>
                            )}


                            {/* Buttons */}
                            <div className="button-models mt-4">
                                <button className="model-button model-button-cancel" onClick={handleClose}>
                                    Cancel
                                </button>

                                <button className={`model-button ${isReject ? 'model-button-delete font-weight500 font-size-heading' : 'model-button-submit'} `} onClick={handleSubmit}>
                                    {isReject
                                        ? "Reject"
                                        : isEditApproval
                                            ? "Approve with Adjustments"
                                            : isFinalize
                                                ? "Finalize"
                                                : "Approve"}
                                </button>
                            </div>

                        </div>
                    </div >
                </Modal.Body >
            </Modal >
        </>
    );
};

const CompensatoryDynamicApprovalActionModal = ({
    i,
    approvalType,
    getDynamicRequestData,
    getApprovalStages
}) => {

    const permission = i?.permissions;

    const PERMISSIONS = {
        APPROVE_REJECT: "Approve-Reject", // Approve, Reject
        APPROVE_REJECT_EDIT: "Approve-Reject-Edit", //Approve, Edit-Approve, Reject
        FINALIZE: "Finalize", // Finalize, Reject
        FINALIZE_EDIT: "Finalize-Edit", //Finalize, Edit-Finalize, Reject
    };

    const actionConfig = {
        [PERMISSIONS.APPROVE_REJECT]: ["approve", "reject"],
        [PERMISSIONS.APPROVE_REJECT_EDIT]: ["approve", "edit", "reject"],
        [PERMISSIONS.FINALIZE]: ["edit", "finalize", "reject"],
        [PERMISSIONS.FINALIZE_EDIT]: ["finalize", "edit", "reject"],
    };

    const allowedActions = actionConfig[permission] || [];

    const [action, setAction] = useState(null);

    const [show, setShow] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({


    });

    const isReject = action === "reject";
    // const isEditApproval = action === "edit";
    const isFinalize = action === "finalize";
    const isApprove = action === "approve";

    const requiresRemarks = isReject;


    const handleShow = () => {
        setRemarks("");
        setErrors({});
        setShow(true);
    };


    const handleClose = () => setShow(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };


    const validateForm = () => {
        const err = {};
        if (requiresRemarks && !remarks.trim()) {
            err.remarks = "Remarks are required!";
        }
        // if (isEditApproval) {
        //     ["remark"].forEach((field) => {
        //         if (!formData[field]) {
        //             err[field] = `${field.replace(/_/g, " ")} is required!`;
        //         }
        //     });
        // }



        setErrors(err);
        return Object.keys(err).length === 0;
    };


    const API_URL = `${BASE_URL}/wfm/dynamicapproval-compensatory-all-approval/${i.id}/`


    const handleSubmit = async () => {
        if (!validateForm()) return;

        const loadingToast = toast.loading("Processing...");



        let payload = {
            approval_status: isReject ? "rejected" : "approved",
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem("emp_code")
        };

        if (requiresRemarks) {
            payload.remarks = remarks.trim();
        }



        try {
            const res = await axios.put(API_URL, payload);

            if (res.status === 200 || res.status === 201) {

                toast.success(
                    isReject
                        ? "Request Rejected"
                        : isFinalize
                            ? "Request Finalized"
                            // :
                            // isEditApproval
                            //     ? "Approved with Adjustments"
                            : "Request Approved"
                );

                await getDynamicRequestData();
                await getApprovalStages();
            } else {
                toast.error(`Error: ${res.statusText}`);
            }
        } catch (err) {
            handleErrorToast(err, loadingToast);
        } finally {
            toast.dismiss(loadingToast);
            handleClose();
        }
    };

    const summaryComponentMap = {
        compensatory: CompensatoryLeaveSummary,
    };

    const SummaryComponent =
        summaryComponentMap[approvalType] || '';


    const actionButtonConfig = {
        approve: {
            title: "Approve",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        // edit: {
        //     title: "Edit & Approve",
        //     className: "model-partial-button",
        //     icon: <WarnYellow />,
        // },
        finalize: {
            title: "Finalize",
            className: "model-edit-button-ml",
            icon: <CheckTick />,
        },
        reject: {
            title: "Reject",
            className: "model-delete-button",
            icon: <CrossRed />,
        },
    };


    return (
        <>

            {allowedActions.map((actionType) => {
                const config = actionButtonConfig[actionType];
                if (!config) return null;

                return (
                    <button
                        key={actionType}
                        className={config.className}
                        title={config.title}
                        onClick={() => {
                            setAction(actionType);
                            handleShow();
                        }}
                    >
                        {config.icon}
                    </button>
                );
            })}

            <Modal show={show} onHide={handleClose} dialogClassName="custom-modal">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">

                            {/* Heading section */}
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                {isApprove && (
                                    <>
                                        {`Confirm Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.compensatory_request.empcode}-${i.compensatory_request.name}'s Short leave`}
                                    </>
                                )}
                                {/* {isEditApproval && (
                                    <>
                                        {`Edit & Approve Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.compensatory_request.empcode}-${i.compensatory_request.name}'s Short leave`}
                                    </>

                                )} */}
                                {isReject && (
                                    <>
                                        {`Reject Stage ${i.level} : ${i.role}`} Approval for
                                        <br />
                                        {`${i.compensatory_request.empcode}-${i.compensatory_request.name}'s Short leave`}
                                    </>
                                )}
                                {isFinalize && (
                                    <>
                                        {`Finalize Stage ${i.level} : ${i.role}`}  Approval for
                                        <br />
                                        {`${i.compensatory_request.empcode}-${i.compensatory_request.name}'s Short leave`}
                                    </>
                                )}

                            </h6>


                            {
                                // !isEditApproval &&
                                <CompensatoryLeaveSummary form={i.compensatory_request} />
                            }


                            {(isReject) && (
                                <div className="mt-3 flex flex-column">
                                    <label className="form-labels font-weight500">
                                        {isReject ? "Rejection Remarks" : "Remarks"}  <span className="required">*</span>
                                    </label>
                                    <textarea
                                        rows="3"
                                        name='remarks'
                                        placeholder={isReject ? "Enter reason for rejection" : "Enter remarks for amount adjustments"}
                                        className={`form-input-textarea ${errors.remarks ? "error" : ""}`}
                                        value={remarks}
                                        style={{ width: 'auto' }}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    ></textarea>
                                    {errors.remarks && (
                                        <div className="error-message">{errors.remarks}</div>
                                    )}
                                </div>
                            )}


                            {/* Buttons */}
                            <div className="button-models mt-4">
                                <button className="model-button model-button-cancel" onClick={handleClose}>
                                    Cancel
                                </button>

                                <button className={`model-button ${isReject ? 'model-button-delete font-weight500 font-size-heading' : 'model-button-submit'} `} onClick={handleSubmit}>
                                    {isReject
                                        ? "Reject"
                                        // : isEditApproval
                                        //     ? "Approve with Adjustments"
                                        : isFinalize
                                            ? "Finalize"
                                            : "Approve"}
                                </button>
                            </div>

                        </div>
                    </div >
                </Modal.Body >
            </Modal >
        </>
    );
};

export {
    DynamicRequestLogTimeline,

    DynamicRequestImprestApproval,
    DynamicRequestImprestEditApproval,
    DynamicRequestImprestRejection,
    DynamicRequestImprestFinalize,

    DynamicRequestExpenseApproval,
    DynamicRequestExpensePartialEdit,
    DynamicRequestExpenseFinalize,
    DynamicRequestExpenseRejection,

    DynamicRequestOfficeApproval,

    DynamicRequestOfficeRejection,

    DynamicRequestVehicleApproval,
    DynamicRequestVehicleRejection,
    DynamicBillingFinal,
    DynamicRequestOfficeEditApproval,
    DynamicRequestVehicleEditApproval,
    DynamicVehicleFinal,

    DynamicRequestTenderApproval,
    DynamicRequestTenderEditApproval,
    DynamicRequestTenderRejection,
    DynamicRequestTenderFinalize,

    DynamicRequestBankDetailsApproval,
    DynamicRequestBankDetailsEditApproval,
    DynamicRequestBankDetailsRejection,
    DynamicRequestBankDetailsFinalize,

    InvestmentDeclarationSummary,
    InvestmentProofSummary,

    // DynamicRequestInvestmentDeclarationApproval,
    // DynamicRequestInvestmentDeclarationEditApproval,
    // DynamicRequestInvestmentDeclarationFinalize,
    // DynamicRequestInvestmentDeclarationRejection,

    DynamicRequestInvestmentProofApproval,
    DynamicRequestInvestmentProofEditApproval,
    DynamicRequestInvestmentProofFinalize,
    DynamicRequestInvestmentProofRejection,
    DynamicApprovalInvestmentDeclarationAction,

    ArrearSummary,
    ArrearDynamicApprovalActionModal,
    DynamicApprovalInvestmentProofsAction,
    BankDetailsDynamicApprovalActionModal,
    BankSummary,

    CompensatoryLeaveSummary,
    CompensatoryDynamicApprovalActionModal,
}
